function semaforoEconomico(marginePercent) {
  if (marginePercent >= 30) return { colore: "#059669", bg: "#d1fae5", label: "Ottimo",     emoji: "🟢" };
  if (marginePercent >= 15) return { colore: "#d97706", bg: "#fff7ed", label: "Attenzione", emoji: "🟡" };
  return { colore: "#dc2626", bg: "#fee2e2", label: "Critico",    emoji: "🔴" };
}

import React, { useEffect, useState, useMemo } from "react";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { useCanSee } from "../hooks/useCanSee";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, serverTimestamp, getDoc, getDocs, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { logModifica, CAMPI_LABEL } from "../utils/logModifica";
import { useToast } from "../utils/Toast";
import { parseValutaItaliana, formatValutaItaliana } from "../utils/clienteHelpers";
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronUp, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { calcolaTotaliEO } from "./ElaborazioneOffertaTab";
import { calcolaSAL } from "../utils/calcoloSAL";
import { costoNoleggio, trasportiNoleggio, giorniTrascorsiNoleggio, giorniFatturabiliDiNoleggio, toLocalDateStr, TIPI_MEZZO_OPERA, SOGLIA_GIORNI_PROMEMORIA } from "../utils/noleggiUtils";
import { STATI_ODA_CONTEGGIATI, imponibileOda } from "../utils/cabinaDiRegia";
import { numIt, eRicavo, marginePrevistoCommessa, totaliCommessa } from "../utils/economiaCommessa";
import { riepilogoVarianti, stessoRiepilogo } from "../utils/varianti";
import { sincronizzaVariantiSuBudget } from "../utils/variantiSync";
import { orePerOperatoreRapportino } from "../utils/oreRapportini";
import { uidConDiario, costoDiario } from "../utils/diarioOre";
import { useControlloDoppioni, RiquadroDoppioniCommessa } from "./ControlloDoppioni";
import ContabilitaLeMieOre from "./ContabilitaLeMieOre";

// ─── CATEGORIE PREDEFINITE ──────────────────────────────────────────────
// Struttura generica: ogni categoria ha un id, label, icona e campi custom.
// Per aggiungere una nuova categoria in futuro basta aggiungere un oggetto qui.
// Valore di una cella della tabella dei costi: numeri all'italiana (100,46), date gg/mm/aaaa
function valoreCella(v, tipo) {
  if (v === undefined || v === null || v === "") return "—";
  if (tipo === "number") {
    const n = numIt(v);
    if (n === 0 && !/^[\s0.,-]*$/.test(String(v))) return String(v); // testo non numerico: com'è
    return n.toLocaleString("it-IT", { maximumFractionDigits: 2 });
  }
  if (tipo === "date" && /^\d{4}-\d{2}-\d{2}$/.test(String(v))) return String(v).split("-").reverse().join("/");
  return v;
}

// Voci scritte da Nexiora a partire da altre sezioni (ottobre 2026): in tabella niente matita
// né cestino — si correggono dove nascono, così il costo resta uguale al documento d'origine.
// Restituisce la provenienza da mostrare, o null per le voci inserite a mano.
export function origineAutomatica(m) {
  if (!m) return null;
  if (m.notaSpeseRef) return { etichetta: "da Nota spese", dove: "Si modifica nella nota spese (Nota Spese / Approvazione Note Spese)." };
  if (m.origineRientroId) return { etichetta: "da Rientro", dove: "Si modifica nel Rientro materiali." };
  if (m.presenzeRef) return { etichetta: "da Presenze", dove: "Si modifica nel cartellino (Presenze)." };
  if (m.origine === "magazzino" || (m.categoriaId === "materiali" && m.dati?.fornitore === "Magazzino interno"))
    return { etichetta: "da Magazzino", dove: "Costo registrato all'evasione della richiesta al magazzino." };
  if (m.dati?.interventoId) return { etichetta: "da Manutenzione", dove: "Si modifica nell'intervento di manutenzione." };
  return null;
}

export const CATEGORIE_DEFAULT = [
  { id: "personale",  label: "Manodopera",      icon: "👷", colore: "#7c3aed",
    campi: [
      { id: "dipendente",    label: "Dipendente/Operatore", tipo: "text"   },
      { id: "data",          label: "Data",                 tipo: "date"   },
      { id: "oreOrdinarie",  label: "Ore ordinarie",        tipo: "number" },
      { id: "oreStraord",    label: "Straordinari",         tipo: "number" },
      { id: "costoOrario",   label: "Costo orario (€)",     tipo: "number" },
    ],
    calcola: (dati) => {
      const ore = (numIt(dati.oreOrdinarie)||0) + (numIt(dati.oreStraord)||0);
      return ore * (numIt(dati.costoOrario)||0);
    },
  },
  { id: "materiali",  label: "Materiali",       icon: "🔧", colore: "#0891b2",
    campi: [
      { id: "data",           label: "Data spesa",            tipo: "date"   },
      { id: "descrizione",    label: "Descrizione/Materiale", tipo: "text"   },
      { id: "fornitore",      label: "Fornitore",             tipo: "text"   },
      { id: "quantita",       label: "Quantità",              tipo: "number" },
      { id: "prezzoUnitario", label: "Prezzo unitario IVA esclusa (€)", etichettaTabella: "Prezzo unit. €", tipo: "number" },
    ],
    calcola: (dati) => (numIt(dati.quantita)||0) * (numIt(dati.prezzoUnitario)||0),
    nota: "⚠️ Inserire prezzi IVA esclusa. Non inserire materiali già acquistati tramite ODA.",
  },
  { id: "trasporti",  label: "Trasporti",        icon: "🚗", colore: "#059669",
    campi: [
      { id: "tipo",      label: "Tipo (carburante/pedaggio/altro)", etichettaTabella: "Tipo", tipo: "text"   },
      { id: "descrizione", label: "Descrizione",                             tipo: "text"   },
      { id: "importo",   label: "Importo (€)",                              tipo: "number" },
    ],
    calcola: (dati) => numIt(dati.importo)||0,
  },
  { id: "vitto",      label: "Vitto e alloggio", icon: "🏨", colore: "#d97706",
    campi: [
      { id: "tipo",        label: "Tipo (hotel/pranzo/cena/altro)", tipo: "text"   },
      { id: "descrizione", label: "Descrizione",                    tipo: "text"   },
      { id: "importo",     label: "Importo (€)",                    tipo: "number" },
    ],
    calcola: (dati) => numIt(dati.importo)||0,
  },
  { id: "professionisti", label: "Professionisti esterni", icon: "🎓", colore: "#8b5cf6",
    campi: [
      { id:"descrizione", label:"Descrizione/Prestazione", tipo:"text"   },
      { id:"professionista", label:"Professionista",       tipo:"text"   },
      { id:"importo",     label:"Importo (€)",             tipo:"number" },
    ],
    calcola: (dati) => numIt(dati.importo)||0,
  },
  { id: "pratiche",   label: "Pratiche",           icon: "📋", colore: "#0e7490",
    campi: [
      { id:"descrizione", label:"Descrizione pratica", tipo:"text"   },
      { id:"importo",     label:"Importo (€)",         tipo:"number" },
    ],
    calcola: (dati) => numIt(dati.importo)||0,
  },
  { id: "noleggi_mezzi_opera", label: "Noleggi mezzi d'opera", icon: "🏗️", colore: "#c2410c",
    campi: [], calcola: () => 0, readOnly: true,
  },
  { id: "varie",      label: "Varie",              icon: "📦", colore: "#64748b",
    campi: [
      { id:"descrizione", label:"Descrizione",  tipo:"text"   },
      { id:"importo",     label:"Importo (€)",  tipo:"number" },
    ],
    calcola: (dati) => numIt(dati.importo)||0,
  },
  { id: "nota_spese", label: "Note spese",        icon: "🧾", colore: "#0891b2",
    campi: [
      { id:"descrizione", label:"Descrizione",  tipo:"text"   },
      { id:"importo",     label:"Importo (€)",  tipo:"number" },
    ],
    calcola: (dati) => numIt(dati.importo)||0,
    readOnly: true,
  },
];

// ─── MODAL INSERIMENTO MOVIMENTO ───────────────────────────────────────
// Definito fuori dal componente padre per evitare il bug di perdita focus
function RigaInput({ campo, value, onChange }) {
  return (
    <div className="form-group" style={{marginBottom:10}}>
      <label className="form-label">{campo.label}</label>
      <input className="input" type={campo.tipo==="number"?"text":"text"}
        inputMode={campo.tipo==="number"?"decimal":"text"}
        value={value||""} onChange={e=>onChange(campo.id, e.target.value)}
        placeholder={campo.tipo==="date"?"gg/mm/aaaa":campo.tipo==="number"?"0":""}/>
    </div>
  );
}

function MovimentoModal({ categoria, movimento, projectId, clienteId, orgId, currentUser, onClose, dipendenti }) {
  const { addToast } = useToast();
  const [dati, setDati] = useState(movimento?.dati || {});
  const [saving, setSaving] = useState(false);
  // isModifica: true quando si sta modificando un movimento già salvato
  // In questo caso il costoOrario è storico e non va sovrascritto automaticamente
  const isModifica = !!movimento?.id;

  const updateDato = (id, val) => setDati(d => ({ ...d, [id]: val }));
  const importoCalcolato = useMemo(() => categoria.calcola(dati), [dati, categoria]);

  const { verifica, finestra: finestraDoppioni } = useControlloDoppioni({ mostraPrezzi: true }); // il Controllo Economico mostra già gli importi
  const submit = async e => {
    e.preventDefault();
    if (!isModifica && categoria.campi.some(c => c.id === "dipendente") && !(dipendenti||[]).some(d => d.id === dati.dipendenteId)) {
      addToast("Scegli la persona dall'elenco: solo chi ha l'account Nexiora collegato alla scheda.", "error");
      return;
    }
    setSaving(true);
    try {
      // Materiali: c'è già qualcosa di uguale in un ODA, una NF, una richiesta al magazzino? (W408)
      let campiDoppioni = {};
      if (categoria.id === "materiali") {
        const esito = await verifica({ orgId, projectId, righe: [dati], escludiDoc: movimento?.id ? `man:${movimento.id}` : null, nonDoppioni: movimento?.nonDoppioni || [] });
        if (!esito.procedi) { setSaving(false); return; }
        campiDoppioni = esito.campi;
      }
      const payload = {
        ...campiDoppioni,
        projectId, clienteId: clienteId||"", organizationId: orgId,
        tipo: "costo",
        categoriaId: categoria.id,
        categoriaLabel: categoria.label,
        dati,
        importo: importoCalcolato,
        modificataDa: currentUser.uid,
        modificataAt: serverTimestamp(),
      };
      if (movimento?.id) {
        await updateDoc(doc(db,"movimentiEconomici",movimento.id), payload);
        addToast("Movimento aggiornato", "success");
      } else {
        await addDoc(collection(db,"movimentiEconomici"), {
          ...payload,
          creadaDa: currentUser.uid,
          creadaAt: serverTimestamp(),
        });
        addToast("Movimento aggiunto", "success");
      }
      onClose();
    } catch(err) { addToast(messaggioErrore(err, "salvare il movimento"), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:440}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>{categoria.icon} {movimento?"Modifica":"Aggiungi"} {categoria.label}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <form onSubmit={submit}>
          {categoria.campi.map(campo => {
            // Per il campo "dipendente" nella categoria personale, usa dropdown se ci sono dipendenti
            // Solo persone con account Nexiora collegato e scheda attiva (W434): le ore e i costi
            // di una persona arrivano sempre tramite il suo account, mai da un nome scritto a mano.
            if (campo.id === "dipendente" && dipendenti) {
              // value = "id::nome cognome" per evitare conflitti tra omonimi. Nei dati si salva il
              // nome (come sempre) e l'id: prima la tendina cercava il solo nome tra valori
              // «id::nome» e tornava su «Seleziona dipendente…» come se la scelta non fosse presa.
              const nomeDi = (d) => `${d.nome||""} ${d.cognome||""}`.trim();
              const scelto = dipendenti.find(d=>d.id===dati.dipendenteId) || dipendenti.find(d=>nomeDi(d)===dati[campo.id]);
              const libero = !scelto && dati[campo.id] ? dati[campo.id] : "";   // voce vecchia con un nome non in anagrafica
              const valore = scelto ? `${scelto.id}::${nomeDi(scelto)}` : libero ? `::${libero}` : "";
              return (
                <div key={campo.id} className="form-group" style={{marginBottom:10}}>
                  <label className="form-label">{campo.label}</label>
                  <select className="input" value={valore} onChange={e => {
                    const [dipId, ...nomeParts] = e.target.value.split("::");
                    const nomeCompleto = nomeParts.join("::");
                    const dip = dipendenti.find(d=>d.id===dipId);
                    updateDato(campo.id, nomeCompleto);
                    updateDato("dipendenteId", dipId || "");
                    // Auto-popola il costo orario SOLO per nuovi movimenti
                    // In modifica il costoOrario storico è già nei dati e non va sovrascritto
                    if (!isModifica && dip?.costoOrario) updateDato("costoOrario", String(dip.costoOrario));
                  }}>
                    <option value="">Seleziona dipendente...</option>
                    {libero && <option value={`::${libero}`}>{libero}</option>}
                    {dipendenti.map(d=>{
                      const nomeCompleto = `${d.nome||""} ${d.cognome||""}`.trim();
                      return (
                        <option key={d.id} value={`${d.id}::${nomeCompleto}`}>
                          {nomeCompleto} {d.ruolo?`(${d.ruolo})`:""} {d.costoOrario?`— €${d.costoOrario}/h`:""}
                        </option>
                      );
                    })}
                  </select>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                    💡 Qui ci sono le persone con account Nexiora collegato alla scheda in <strong>Dipendenti & Collaboratori</strong>.
                  </div>
                </div>
              );
            }
            // Campo costoOrario: read-only in modifica (protezione costo storico)
            if (campo.id === "costoOrario" && isModifica) {
              return (
                <div key={campo.id} className="form-group" style={{marginBottom:10}}>
                  <label className="form-label">{campo.label}</label>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <input className="input" type="text" value={dati[campo.id]||""} readOnly
                      style={{background:"#f9fafb",color:"#6b7280",cursor:"not-allowed",flex:1}}/>
                    <span style={{fontSize:10,color:"#9ca3af",whiteSpace:"nowrap"}}>🔒 storico</span>
                  </div>
                  <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>
                    Il costo orario è bloccato al valore applicato al momento dell'inserimento per garantire la correttezza dei calcoli storici.
                    Per cambiarlo elimina questa voce e reinseriscila.
                  </div>
                </div>
              );
            }
            return <RigaInput key={campo.id} campo={campo} value={dati[campo.id]} onChange={updateDato}/>;
          })}
          {importoCalcolato > 0 && (
            <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:13,color:"#6b7280"}}>Importo calcolato</span>
              <span style={{fontSize:16,fontWeight:800,color:"#059669"}}>€ {formatValutaItaliana(importoCalcolato)}</span>
            </div>
          )}
          {finestraDoppioni}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving||importoCalcolato<=0}>
              {saving?"Salvo...":"Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Avviso sotto «Manodopera»: dice da dove arrivano DAVVERO le ore, secondo la fonte scelta
// in Impostazioni (prima diceva sempre «Programmazione Settimanale», anche con il cartellino).
const FONTI_ORE = {
  presenze: ["dai cartellini presenze approvati", "di chi non ha il cartellino in Nexiora"],
  rapportini: ["dai rapportini", "ore non scritte in nessun rapportino"],
  programmazione: ["dalla Programmazione Settimanale", "ore lavorate ma non pianificate"],
};
export function testoManodopera(fonte, conDiario = false) {
  const [da, esempio] = FONTI_ORE[fonte] || FONTI_ORE.programmazione;
  return `Le ore arrivano da sole ${da}${conDiario ? " e, per chi le segna lì, da «Le mie ore»" : ""}: non vanno ripetute qui. Aggiungi solo quelle che non passano da lì (per esempio ${esempio}).`;
}

// ─── SEZIONE CATEGORIA ─────────────────────────────────────────────────
function SezioneCategoria({ categoria, movimenti, projectId, clienteId, orgId, currentUser, canEdit, dipendenti, orePerDipendente, costiOda, rientriNonApplicati, orgSettings={}, noleggiMezziOpera=[], canChiudereNoleggio=false, onApriChiusuraNoleggio }) {
  const [aperta, setAperta] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMovimento, setEditMovimento] = useState(null);
  const { addToast } = useToast();

  // Il totale della card deve coincidere col KPI "Manodopera" in cima pagina — include quindi
  // anche le ore aggregate (Programmazione/Rapportini/Presenze) mostrate nella tabella verde
  // sopra, non solo le voci inserite manualmente. Nessun doppio conteggio: quelle ore non sono
  // mai incluse in "movimenti" (per fonte Presenze hanno categoriaId="manodopera", diverso da
  // questa categoria "personale"; per Programmazione/Rapportini non sono nemmeno movimenti reali).
  const totaleOreAggregate = useMemo(() => (orePerDipendente||[]).reduce((s,d) => s + (d.totale||0), 0), [orePerDipendente]);
  const totaleNoleggiCard = useMemo(() => (noleggiMezziOpera||[]).reduce((s,n) => s + costoNoleggio(n), 0), [noleggiMezziOpera]);
  const totale = useMemo(() => movimenti.reduce((s,m) => s + (numIt(m.importo)||0), 0) + totaleOreAggregate
    + (categoria.id==="noleggi_mezzi_opera" ? totaleNoleggiCard : 0), [movimenti, totaleOreAggregate, totaleNoleggiCard, categoria.id]);

  const coloreCat = /^#[0-9a-fA-F]{6}$/.test(categoria.colore || "") ? categoria.colore : "#6b7280";
  const elimina = async id => {
    if (!await confirm("Eliminare questo movimento?", { type:"danger" })) return;
    await deleteDoc(doc(db,"movimentiEconomici",id));
    addToast("Eliminato","success");
  };

  return (
    // Cornice colorata e intestazione tinta (ottobre 2026, come la Cabina di Regia): ogni
    // categoria ha il suo colore, così i riquadri si distinguono a colpo d'occhio
    <div style={{background:"#fff",borderLeft:`1.5px solid ${coloreCat}55`,borderRight:`1.5px solid ${coloreCat}55`,borderBottom:`1.5px solid ${coloreCat}55`,
      borderTop:`4px solid ${coloreCat}`,borderRadius:12,marginBottom:12,overflow:"hidden",boxShadow:"0 4px 14px rgba(17,24,39,.07)"}}>
      {/* Header sezione */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",background:`${coloreCat}0d`,cursor:"pointer",borderBottom: aperta&&movimenti.length>0?`1px solid ${coloreCat}33`:"none"}}
        onClick={()=>setAperta(!aperta)}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18}}>{categoria.icon}</span>
          <div>
            <span style={{fontSize:14,fontWeight:800,color:coloreCat}}>{categoria.label}</span>
            <span style={{fontSize:12,color:"#9ca3af",marginLeft:6}}>({movimenti.length} voci)</span>
            {categoria.id==="materiali" && (
              <div style={{fontSize:11,color:"#0891b2",marginTop:2,fontStyle:"italic"}}>
                ⚠️ Inserire prezzi IVA esclusa. Non duplicare materiali già presenti in un ODA — quelli vengono importati automaticamente.
              </div>
            )}
            {categoria.id==="personale" && (
              <div style={{fontSize:11,color:"#7c3aed",marginTop:2,fontStyle:"italic"}}>
                {testoManodopera(orgSettings?.fontOreEconomico, (orePerDipendente||[]).some(r => r.fonte === "diario"))}
              </div>
            )}
            {categoria.id==="nota_spese" && (
              <div style={{fontSize:11,color:"#0891b2",marginTop:2,fontStyle:"italic"}}>
                Importi generati automaticamente dalle Note Spese approvate con questa commessa come riferimento. Non modificare manualmente.
              </div>
            )}
            {categoria.id==="noleggi_mezzi_opera" && (
              <div style={{fontSize:11,color:"#c2410c",marginTop:2,fontStyle:"italic"}}>
                Calcolato automaticamente dal Registro Noleggi (gru, piattaforme, altri mezzi d'opera collegati a questa commessa). Non modificabile qui.
              </div>
            )}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:15,fontWeight:800,color: totale>0?categoria.colore:"#9ca3af"}}>
            € {formatValutaItaliana(totale)}
          </span>
          {canEdit && (
            <button type="button" onClick={e=>{e.stopPropagation();setEditMovimento(null);setShowModal(true);}}
              style={{display:"flex",alignItems:"center",gap:4,background:categoria.colore,color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              <Plus size={12}/> Aggiungi
            </button>
          )}
          {aperta ? <ChevronUp size={16} color="#9ca3af"/> : <ChevronDown size={16} color="#9ca3af"/>}
        </div>
      </div>

      {/* Righe movimenti */}
      {/* BLOCCO NOLEGGI: solo categoria noleggi_mezzi_opera — mai inserito a mano, calcolato
          da Registro Noleggi. Promemoria persistente oltre i 30gg attivo, con chiusura a
          data pregressa possibile direttamente da qui (stesso permesso di Registro Noleggi:
          solo titolare/amministrazione, un pm vede la tabella ma non il bottone Chiudi). */}
      {aperta && categoria.id === "noleggi_mezzi_opera" && (
        <div style={{padding:"8px 16px"}}>
          {noleggiMezziOpera.length === 0 ? (
            <div style={{fontSize:12,color:"#9ca3af",padding:"6px 0"}}>Nessun mezzo d'opera a noleggio registrato per questa commessa.</div>
          ) : (
            <>
              {noleggiMezziOpera.filter(n => !n.dataFineEffettiva && giorniTrascorsiNoleggio(n.dataInizio,null) > SOGLIA_GIORNI_PROMEMORIA).map(n => (
                <div key={"prom_"+n.id} style={{marginBottom:8,background:"#fffbeb",border:"1px solid #fde68a",borderLeft:"3px solid #f59e0b",borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,color:"#92400e"}}>
                    ⏳ <strong>{TIPI_MEZZO_OPERA[n.tipo]?.label||n.tipo}</strong> attivo da <strong>{giorniTrascorsiNoleggio(n.dataInizio,null)} giorni</strong> (dal {new Date(n.dataInizio+"T00:00:00").toLocaleDateString("it-IT")}) — se è terminato, chiudilo per fermare il costo.
                  </span>
                  {canChiudereNoleggio && (
                    <button onClick={()=>onApriChiusuraNoleggio&&onApriChiusuraNoleggio(n)}
                      style={{background:"#f59e0b",color:"#fff",border:"none",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      Chiudi noleggio
                    </button>
                  )}
                </div>
              ))}
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#fff7ed"}}>
                    <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#9a3412"}}>Mezzo</th>
                    <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#9a3412"}}>Fornitore</th>
                    <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#9a3412"}}>Giorni</th>
                    <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#9a3412"}}>Totale</th>
                    <th style={{padding:"4px 10px"}}></th>
                  </tr>
                </thead>
                <tbody>
                  {noleggiMezziOpera.map(n => {
                    const attivo = !n.dataFineEffettiva;
                    const giorni = giorniFatturabiliDiNoleggio(n);
                    return (
                      <tr key={n.id} style={{borderBottom:"1px solid #ffedd5"}}>
                        <td style={{padding:"5px 10px",color:"#374151",fontWeight:500}}>{TIPI_MEZZO_OPERA[n.tipo]?.label||n.tipo}{n.identificativo?` · ${n.identificativo}`:""} {attivo?<span style={{fontSize:9,fontWeight:700,color:"#059669"}}>● ATTIVO</span>:<span style={{fontSize:9,color:"#9ca3af"}}>concluso</span>}</td>
                        <td style={{padding:"5px 10px",color:"#6b7280"}}>{n.fornitore}</td>
                        <td style={{padding:"5px 10px",textAlign:"right",color:"#6b7280"}}>{giorni}</td>
                        <td style={{padding:"5px 10px",textAlign:"right",fontWeight:700,color:"#c2410c"}}>€ {formatValutaItaliana(costoNoleggio(n))}{trasportiNoleggio(n).totale>0 && <div style={{fontSize:9.5,fontWeight:500,color:"#9a3412"}}>di cui trasporti € {formatValutaItaliana(trasportiNoleggio(n).totale)}</div>}</td>
                        <td style={{padding:"5px 10px",textAlign:"right"}}>
                          {attivo && canChiudereNoleggio && (
                            <button onClick={()=>onApriChiusuraNoleggio&&onApriChiusuraNoleggio(n)}
                              style={{background:"none",border:"1px solid #fed7aa",color:"#c2410c",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                              Chiudi
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:6}}>
                💡 Il costo cresce da solo giorno per giorno finché il noleggio resta attivo, più i trasporti di consegna e ritiro del noleggiatore — nessun inserimento manuale. Per aggiungere un nuovo noleggio vai al Registro e gestione noleggi.
              </div>
            </>
          )}
        </div>
      )}

      {/* BLOCCO A: Ore da programmazione settimanale (solo categoria personale) */}
      {aperta && orePerDipendente && orePerDipendente.length > 0 && (
        <div style={{background:"#f0fdf4",borderBottom:"1px solid #bbf7d0",padding:"8px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#065f46",marginBottom:6}}>
            {orgSettings?.fontOreEconomico==="rapportini" ? "📋 Ore da Rapportini (automatico)"
             : orgSettings?.fontOreEconomico==="presenze" ? "🕐 Ore da Presenze approvate (automatico)"
             : "📅 Ore da Programmazione Settimanale (automatico)"}
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#dcfce7"}}>
                <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#166534"}}>Dipendente</th>
                <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#166534"}}>Ore totali</th>
                <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#166534"}}>Costo/h</th>
                <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#166534"}}>Totale</th>
              </tr>
            </thead>
            <tbody>
              {orePerDipendente.map((d,i)=>(
                <tr key={i} style={{borderBottom:"1px solid #bbf7d0"}}>
                  <td style={{padding:"5px 10px",color:"#374151",fontWeight:500}}>{d.nome}</td>
                  <td style={{padding:"5px 10px",textAlign:"right",color:"#374151"}}>{d.ore}h</td>
                  <td style={{padding:"5px 10px",textAlign:"right",color:"#6b7280"}}>€ {formatValutaItaliana(d.costoOrario)}/h</td>
                  <td style={{padding:"5px 10px",textAlign:"right",fontWeight:700,color:"#059669"}}>€ {formatValutaItaliana(Math.round(d.totale*100)/100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>
            {orgSettings?.fontOreEconomico==="presenze"
              ? "💡 Le ore vengono registrate automaticamente all'approvazione del cartellino presenze mensile."
              : orgSettings?.fontOreEconomico==="rapportini"
              ? "💡 Le ore si aggiornano automaticamente dai rapportini compilati. Aggiungi ore manuali sotto."
              : "💡 Le ore si aggiornano automaticamente dalla Programmazione Settimanale. Aggiungi ore manuali sotto."}
          </div>
          {orgSettings?.fontOreEconomico==="presenze" && orePerDipendente.length===0 && (
            <div style={{marginTop:8,padding:"10px 14px",background:"#fffbeb",border:"1px solid #fde68a",borderLeft:"3px solid #f59e0b",borderRadius:8,fontSize:12,color:"#92400e"}}>
              ⏳ <strong>Nessuna ora ancora contabilizzata.</strong> Le ore da presenze vengono calcolate solo dopo che il cartellino mensile viene inviato in approvazione e approvato dal responsabile.
            </div>
          )}

        </div>
      )}

      {aperta && costiOda && costiOda.length > 0 && (
        <div style={{background:"#eff6ff",borderBottom:"1px solid #bfdbfe",padding:"8px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#1e40af",marginBottom:6}}>
            🧾 Costi sostenuti tramite Ordine di Acquisto (ODA) — importati automaticamente
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#dbeafe"}}>
                <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#1e40af"}}>Fornitore</th>
                <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#1e40af"}}>Descrizione</th>
                <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#1e40af"}}>N° ODA</th>
                <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#1e40af"}}>Importo</th>
              </tr>
            </thead>
            <tbody>
              {costiOda.map((nf,i)=>{
                const imp = typeof nf.importo==="number" ? nf.importo
                  : parseFloat((nf.importo||"").replace(/\./g,"").replace(",",".")) || 0;
                return (
                  <tr key={i} style={{borderBottom:"1px solid #bfdbfe"}}>
                    <td style={{padding:"5px 10px",color:"#374151",fontWeight:500}}>{nf.fornitore||"—"}</td>
                    <td style={{padding:"5px 10px",color:"#374151"}}>{nf.descrizione||"—"}</td>
                    <td style={{padding:"5px 10px",color:"#6b7280",fontSize:11}}>{nf.numeroOda||"—"}</td>
                    <td style={{padding:"5px 10px",textAlign:"right",fontWeight:700,color:"#1d4ed8"}}>€ {imp.toLocaleString("it-IT",{minimumFractionDigits:2})}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>
            💡 Questi costi derivano da NF con stato "Inviato". Aggiungi manualmente sotto le spese extra senza ODA.
          </div>
        </div>
      )}

      {aperta && rientriNonApplicati && rientriNonApplicati.length > 0 && (
        <div style={{background:"#fffbeb",borderBottom:"1px solid #fde68a",padding:"8px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#92400e",marginBottom:6}}>
            ⚠️ {rientriNonApplicati.length} materiale/i rientrato/i non considerato/i nel calcolo — valore economico non disponibile
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#fef3c7"}}>
                <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#92400e"}}>Data</th>
                <th style={{padding:"4px 10px",textAlign:"left",fontWeight:600,color:"#92400e"}}>Materiale</th>
                <th style={{padding:"4px 10px",textAlign:"right",fontWeight:600,color:"#92400e"}}>Quantità</th>
              </tr>
            </thead>
            <tbody>
              {rientriNonApplicati.map(r=>(
                <tr key={r.id} style={{borderBottom:"1px solid #fde68a"}}>
                  <td style={{padding:"5px 10px",color:"#78350f"}}>{r.dataRientro||"—"}</td>
                  <td style={{padding:"5px 10px",color:"#78350f",fontWeight:600}}>{r.descrizione}</td>
                  <td style={{padding:"5px 10px",textAlign:"right",color:"#78350f"}}>{r.quantita} {r.unitaMisura}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{fontSize:11,color:"#92400e",marginTop:4}}>
            💡 Questi materiali sono stati registrati in Rientro Materiali ma non hanno un prezzo unitario noto (né nel Listino Materiali, né in un movimento economico già inserito) — il costo della commessa non è stato ridotto per evitare stime non affidabili.
          </div>
        </div>
      )}

      {aperta && movimenti.length > 0 && (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#f3f4f6"}}>
                {/* intestazioni brevi in tabella (il nome completo resta nel modulo e nel suggerimento);
                    numeri centrati sotto l'intestazione */}
                {categoria.campi.map(c => (
                  <th key={c.id} title={c.etichettaTabella ? c.label : undefined}
                    style={{padding:"6px 12px",textAlign:c.tipo==="number"?"center":"left",fontWeight:600,color:"#6b7280",whiteSpace:"nowrap"}}>{c.etichettaTabella || c.label}</th>
                ))}
                <th style={{padding:"6px 12px",textAlign:"right",fontWeight:600,color:"#6b7280"}}>Importo</th>
                {canEdit && <th style={{width:60}}></th>}
              </tr>
            </thead>
            <tbody>
              {movimenti.map((m,i) => (
                <tr key={m.id} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                  {categoria.campi.map(c => (
                    <td key={c.id} style={{padding:"8px 12px",color:"#374151",whiteSpace:"nowrap",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",
                      textAlign:c.tipo==="number"?"center":"left",fontVariantNumeric:c.tipo==="number"?"tabular-nums":undefined}}>
                      {valoreCella(m.dati?.[c.id], c.tipo)}
                    </td>
                  ))}
                  <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,color:"#059669",whiteSpace:"nowrap"}}>
                    € {formatValutaItaliana(m.importo)}
                  </td>
                  {canEdit && (origineAutomatica(m) ? (
                    <td style={{padding:"8px 12px",textAlign:"right",whiteSpace:"nowrap"}}>
                      <span title={`Voce automatica: ${origineAutomatica(m).dove}`}
                        style={{fontSize:10.5,fontWeight:700,color:"#6b7280",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:10,padding:"2px 8px",cursor:"help"}}>
                        🔒 {origineAutomatica(m).etichetta}
                      </span>
                    </td>
                  ) : (
                    <td style={{padding:"8px 12px",display:"flex",gap:4,justifyContent:"flex-end"}}>
                      <button onClick={()=>{setEditMovimento(m);setShowModal(true);}} className="btn-icon" style={{padding:4}}><Edit2 size={12}/></button>
                      <button onClick={()=>elimina(m.id)} className="btn-icon" style={{padding:4,color:"#dc2626"}}><Trash2 size={12}/></button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aperta && movimenti.length === 0 && (
        <div style={{padding:"16px 18px",textAlign:"center",color:"#9ca3af",fontSize:13}}>
          Nessuna voce inserita — {canEdit?"clicca \"Aggiungi\" per iniziare":"nessun dato disponibile"}
        </div>
      )}

      {showModal && (
        <MovimentoModal categoria={categoria} movimento={editMovimento}
          projectId={projectId} clienteId={clienteId} orgId={orgId} currentUser={currentUser}
          dipendenti={(dipendenti||[]).filter(d => d.attivo !== false && d.nexoraUid && (d.tipo||"dipendente") === "dipendente")}
          onClose={()=>{setShowModal(false);setEditMovimento(null);}}/>
      )}
    </div>
  );
}


// ─── SEZIONE SUBAPPALTATORI / PROFESSIONISTI ──────────────────────────
function SubappaltatoriSection({ movimenti, dipendenti, projectId, clienteId, orgId, currentUser, canEdit }) {
  const [aperta, setAperta] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nome:"", importo:"", descrizione:"", nota:"", subappaltatoreId:"" });
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  // Ditte esterne in anagrafica (W405): il costo si collega alla ditta, così il lavoro
  // affidato a ciascuna si può confrontare (Gestione → Ditte esterne)
  const [ditte, setDitte] = useState([]);
  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(query(collection(db,"subappaltatori"), where("organizationId","==",orgId)),
      s => setDitte(s.docs.map(d => ({ id:d.id, ...d.data() })).filter(d => d.attivo !== false).sort((a,b) => (a.ragioneSociale||"").localeCompare(b.ragioneSociale||""))),
      () => setDitte([]));
  }, [orgId]);

  const totale = useMemo(()=>movimenti.reduce((s,m)=>s+(numIt(m.importo)||0),0),[movimenti]);

  const salva = async () => {
    if (saving) return; // protezione doppio click — corretta oggi
    if (!form.nome.trim()) { addToast("Nome obbligatorio","error"); return; }
    const importo = parseValutaItaliana(form.importo);
    if (importo <= 0) { addToast("Inserisci un importo valido","error"); return; }
    setSaving(true);
    try {
      await addDoc(collection(db,"movimentiEconomici"),{
        projectId, clienteId:clienteId||"", organizationId:orgId,
        tipo:"costo", categoriaId:"subappaltatore", categoriaLabel:"Subappaltatori",
        dati:{ nome:form.nome, descrizione:form.descrizione, nota:form.nota },
        ...(form.subappaltatoreId ? { subappaltatoreId: form.subappaltatoreId } : {}),
        importo,
        creadaDa:currentUser.uid, creadaAt:serverTimestamp(),
        modificataDa:currentUser.uid, modificataAt:serverTimestamp(),
      });
      setForm({ nome:"", importo:"", descrizione:"", nota:"", subappaltatoreId:"" });
      setShowForm(false);
      addToast("Aggiunto ✅","success");
    } finally {
      setSaving(false);
    }
  };

  const elimina = async id => {
    if (!await confirm("Eliminare?", { type:"danger" })) return;
    await deleteDoc(doc(db,"movimentiEconomici",id));
  };

  return (
    <div style={{background:"#fff",borderLeft:"1.5px solid #05966955",borderRight:"1.5px solid #05966955",borderBottom:"1.5px solid #05966955",borderTop:"4px solid #059669",borderRadius:12,marginBottom:12,overflow:"hidden",boxShadow:"0 4px 14px rgba(17,24,39,.07)"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",background:"#0596690d",borderBottom:"1px solid #05966933",cursor:"pointer"}}
        onClick={()=>setAperta(!aperta)}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18}}>🏗️</span>
          <span style={{fontSize:14,fontWeight:800,color:"#059669"}}>Subappaltatori</span>
          <span style={{fontSize:12,color:"#9ca3af"}}>({movimenti.length} voci)</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:15,fontWeight:800,color:totale>0?"#0891b2":"#9ca3af"}}>€ {formatValutaItaliana(totale)}</span>
          {canEdit && (
            <button type="button" onClick={e=>{e.stopPropagation();setShowForm(!showForm);}}
              style={{display:"flex",alignItems:"center",gap:4,background:"#0891b2",color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              <Plus size={12}/> Aggiungi
            </button>
          )}
          {aperta ? <ChevronUp size={16} color="#9ca3af"/> : <ChevronDown size={16} color="#9ca3af"/>}
        </div>
      </div>

      {aperta && showForm && canEdit && (
        <div style={{padding:"12px 16px",background:"#f0f9ff",borderBottom:"1px solid #bae6fd"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1.5fr auto",gap:8,alignItems:"flex-end"}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:3}}>Ditta o nome *</label>
              {ditte.length>0 && (
                <select className="input" value={form.subappaltatoreId||""} style={{marginBottom:6}}
                  onChange={e=>{ const d = ditte.find(x=>x.id===e.target.value); setForm(f=>({...f, subappaltatoreId: d ? d.id : "", nome: d ? d.ragioneSociale : ""})); }}>
                  <option value="">— Ditta esterna dall'anagrafica —</option>
                  {ditte.map(d=><option key={d.id} value={d.id}>{d.ragioneSociale}</option>)}
                  <option value="">Altro (professionista o nome libero, scritto sotto)</option>
                </select>
              )}
              <div style={{position:"relative", display: form.subappaltatoreId ? "none" : "block"}}>
                <input className="input" value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))}
                  placeholder="Es. Ing. Rossi, Idrotech Srl..."/>
                {dipendenti.length>0 && (
                  <select style={{position:"absolute",top:0,right:0,height:"100%",opacity:0,width:30,cursor:"pointer"}}
                    onChange={e=>setForm(f=>({...f,nome:e.target.value}))}>
                    <option value="">Seleziona...</option>
                    {dipendenti.map(d=><option key={d.id} value={d.nome}>{d.nome}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:3}}>Importo (€) *</label>
              <input className="input" type="text" inputMode="decimal" value={form.importo} onChange={e=>setForm(f=>({...f,importo:e.target.value}))}
                placeholder="Es. 2.500"/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:3}}>Descrizione / Prestazione</label>
              <input className="input" value={form.descrizione} onChange={e=>setForm(f=>({...f,descrizione:e.target.value}))}
                placeholder="Es. Progetto elettrico, Idraulica, Ponteggio..."/>
            </div>
            <button onClick={salva} disabled={saving}
              style={{padding:"8px 16px",background:"#0891b2",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:saving?"default":"pointer",fontFamily:"'Inter',sans-serif",height:38,opacity:saving?0.6:1}}>
              {saving?"...":"Salva"}
            </button>
          </div>
        </div>
      )}

      {aperta && movimenti.length > 0 && (
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:"#f3f4f6"}}>
              <th style={{padding:"6px 12px",textAlign:"left",fontWeight:600,color:"#6b7280"}}>Nome</th>
              <th style={{padding:"6px 12px",textAlign:"left",fontWeight:600,color:"#6b7280"}}>Prestazione</th>
              <th style={{padding:"6px 12px",textAlign:"right",fontWeight:600,color:"#6b7280"}}>Importo</th>
              {canEdit&&<th style={{width:40}}></th>}
            </tr>
          </thead>
          <tbody>
            {movimenti.map((m,i)=>(
              <tr key={m.id} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                <td style={{padding:"8px 12px",fontWeight:600}}>{m.dati?.nome||"—"}{m.subappaltatoreId ? <span title="Collegato alla ditta in anagrafica" style={{marginLeft:6,fontSize:11,color:"#7c3aed"}}>🔗</span> : null}</td>
                <td style={{padding:"8px 12px",color:"#6b7280"}}>{m.dati?.descrizione||"—"}</td>
                <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,color:"#0891b2"}}>€ {formatValutaItaliana(m.importo)}</td>
                {canEdit&&<td style={{padding:"8px 6px"}}>
                  <button onClick={()=>elimina(m.id)} className="btn-icon" style={{color:"#dc2626",padding:3}}><Trash2 size={12}/></button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {aperta && movimenti.length===0 && (
        <div style={{padding:"14px 18px",textAlign:"center",color:"#9ca3af",fontSize:13}}>
          Nessun subappaltatore registrato per questa commessa
        </div>
      )}
    </div>
  );
}

// ─── COMPONENTE PRINCIPALE: ControlloEconomico ─────────────────────────
export default function ControlloEconomico({ projectId, projectData, clienteId, orgId, categoriCustom, onNavigate, onApriVarianti }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const { addToast } = useToast();
  const canEdit = (isOrgAdmin || isPlatformOwner) && projectData?.statoCommessa !== "chiusa"; // Fase 1: solo admin, mai se commessa chiusa

  const [movimenti, setMovimenti] = useState([]);
  const [giorniGiornaleCantiere, setGiorniGiornaleCantiere] = useState([]);
  const [presenzeMap, setPresenzeMap] = useState({}); // chiave `${uid}_${anno}_${mese}` -> doc presenze
  const [mostraDiscrepanzeGP, setMostraDiscrepanzeGP] = useState(false);
  const [budget, setBudget] = useState(null);
  const [budgetDoppi, setBudgetDoppi] = useState(0); // W450: più documenti di budget per la stessa commessa
  const [editBudget, setEditBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ importoCommessa:"", costoPersonalePrev:"", costoMaterialiPrev:"" });
  const [costiMateriali, setCostiMateriali] = useState([]); // NF con stato Inviato per questa commessa
  const [rientriNonApplicati, setRientriNonApplicati] = useState([]); // Rientri Materiali senza valore economico noto
  const [noleggiMezziOpera, setNoleggiMezziOpera] = useState([]); // Noleggi mezzi d'opera di questa commessa — costo calcolato, mai inserito a mano
  const [chiudiNoleggioTarget, setChiudiNoleggioTarget] = useState(null);
  const [dataChiusuraNoleggio, setDataChiusuraNoleggio] = useState("");
  const [savingChiusuraNoleggio, setSavingChiusuraNoleggio] = useState(false);
  const canChiudereNoleggio = isOrgAdmin || isPlatformOwner || canSee("registro-noleggi-chiudi");
  const [savingBudget, setSavingBudget] = useState(false);
  const [categoriExtra, setCategoriExtra] = useState(categoriCustom||[]);
  const [dipendenti, setDipendenti] = useState([]);
  const [oreProgr, setOreProgr] = useState([]); // ore da programmazione settimanale
  const [utentiNexora, setUtentiNexora] = useState([]);
  const [showAddCategoria, setShowAddCategoria] = useState(false);
  const [nuovaCategoria, setNuovaCategoria] = useState({ label:"", icon:"📦" });
  const [suggerimentoBudget, setSuggerimentoBudget] = useState(null); // {importo, fonte} | null

  const ICONE_DISPONIBILI = ["📦","🔩","💻","📋","🏗️","🚛","⚡","🔬","📡","🌿","🏭","💡"];

  // Tutte le categorie attive (predefinite + custom)
  const tutteLeCategorie = useMemo(() => [
    ...CATEGORIE_DEFAULT,
    ...categoriExtra.map(c => ({
      ...c,
      colore: "#6366f1",
      campi: [
        { id:"descrizione", label:"Descrizione", tipo:"text" },
        { id:"importo",     label:"Importo (€)", tipo:"number" },
      ],
      calcola: (dati) => numIt(dati.importo)||0,
    }))
  ], [categoriExtra]);

  // Carica note spese approvate per questa commessa


  // Carica ODA per questa commessa (costi materiali) — collegamento corretto oggi: prima
  // leggeva l'importo dalla NF (un campo piatto, spesso mai compilato, sganciato dalle
  // righe vere costruite nel nuovo ciclo ODA), quindi il Controllo Economico non vedeva
  // mai il costo reale e dettagliato dell'ordine. Ora legge direttamente dall'ODA: usa
  // l'importo della fattura quando è già stata collegata (il costo vero, verificato),
  // altrimenti l'importo dell'ordine calcolato dalle righe (la miglior stima finché non
  // arriva la fattura). Contano solo gli ordini avviati per davvero (da "Inviato" in poi)
  // — una Bozza o un ordine Annullato non rappresentano un costo reale.

  useEffect(() => {
    if (!projectId) return;
    const qOda = query(collection(db,"ordiniAcquisto"), where("commessaId","==",projectId), where("organizationId","==",orgId));
    return onSnapshot(qOda, snap => {
      const lista = snap.docs
        .map(d=>({id:d.id,...d.data()}))
        .filter(o=>!o.eliminato && STATI_ODA_CONTEGGIATI.includes(o.stato))
        .map(o=>({
          fornitore: o.fornitoreNome||"—",
          descrizione: o.righe?.length ? o.righe.map(r=>r.descrizione).filter(Boolean).join(", ") : (o.descrizione||""),
          numeroOda: o.numeroODA||o.numeroOda||null,
          importo: imponibileOda(o), // IVA esclusa, come tutti i costi di commessa
        }));
      setCostiMateriali(lista);
    });
  }, [projectId, orgId]);

  // Noleggi mezzi d'opera di questa commessa — real-time, costo mai scritto a mano.
  // Nota: collezione "noleggi" nel Gruppo A delle regole (campo orgId), quindi la query
  // deve filtrare su orgId come le regole si aspettano (stessa lezione del Controllo Economico).
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db,"noleggi"), where("projectId","==",projectId), where("orgId","==",orgId), where("categoria","==","mezzo_opera"));
    return onSnapshot(q, snap => setNoleggiMezziOpera(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [projectId, orgId]);

  const confermaChiusuraNoleggio = async () => {
    if (!chiudiNoleggioTarget || !dataChiusuraNoleggio) return;
    if (dataChiusuraNoleggio < chiudiNoleggioTarget.dataInizio) { addToast("La data di fine non può essere precedente alla data di inizio","error"); return; }
    setSavingChiusuraNoleggio(true);
    try {
      await updateDoc(doc(db,"noleggi",chiudiNoleggioTarget.id), { dataFineEffettiva: dataChiusuraNoleggio, updatedAt: serverTimestamp() });
      logModifica({ orgId, uid: currentUser?.uid||"", nomeUtente: currentUser?.displayName||"",
        sezione:"noleggi", refId: chiudiNoleggioTarget.id, refNome: `${TIPI_MEZZO_OPERA[chiudiNoleggioTarget.tipo]?.label||chiudiNoleggioTarget.tipo} · ${chiudiNoleggioTarget.fornitore}`,
        campo:"dataFineEffettiva", campoLabel:"Chiusura noleggio", before:"Attivo", after:dataChiusuraNoleggio,
        projectId, projectName: projectData?.name||"" });
      addToast("Noleggio chiuso ✅","success");
      setChiudiNoleggioTarget(null);
    } catch (err) {
      console.error("Errore chiusura noleggio:", err);
      addToast("Errore nella chiusura: "+(err?.message||"errore sconosciuto"),"error");
    } finally {
      setSavingChiusuraNoleggio(false);
    }
  };

  // Carica movimenti real-time
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db,"movimentiEconomici"), where("projectId","==",projectId), where("organizationId","==",orgId));
    return onSnapshot(q, snap => setMovimenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [projectId, orgId]);

  // Rientri Materiali di questa commessa senza valore economico noto — solo a scopo informativo
  // (punto 6 della specifica "Rientro Materiali": segnalare sempre le voci non considerate)
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db,"rientriMateriali"), where("projectId","==",projectId), where("valoreNoto","==",false), where("organizationId","==",orgId));
    return onSnapshot(q, snap => setRientriNonApplicati(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [projectId, orgId]);

  // Carica i giorni del Giornale di Cantiere — SOLO a scopo informativo, non entra mai nei calcoli costi
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db,"giornaleCantiereDays"), where("projectId","==",projectId), where("orgId","==",orgId));
    return onSnapshot(q, snap => setGiorniGiornaleCantiere(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [projectId, orgId]);

  // Carica dipendenti dell'org
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db,"dipendenti"), where("organizationId","==",orgId));
    // Tutte le schede, anche quelle disattivate (W434): chi non lavora più qui ha comunque
    // lavorato sulle commesse passate, e quelle ore devono tenere il loro costo. Nelle
    // tendine per i nuovi inserimenti si mostrano solo le schede attive (dipendentiAttivi).
    return onSnapshot(q, snap => setDipendenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  // Carica le presenze necessarie per il riscontro Giornale vs Presenze reali — solo per i
  // dipendenti e i mesi effettivamente citati nel Giornale di Cantiere di questa commessa.
  useEffect(() => {
    if (!orgId || giorniGiornaleCantiere.length===0 || dipendenti.length===0) { setPresenzeMap({}); return; }
    const combo = new Set(); // "uid|anno|mese" (mese 1-based)
    giorniGiornaleCantiere.forEach(g => {
      if (!g.data) return;
      const [anno, mese] = g.data.split("-");
      (g.manodopera||[]).forEach(r => {
        const nomeCercato = (r.nome||"").trim().toLowerCase();
        if (!nomeCercato) return;
        const dip = dipendenti.find(d => `${d.nome||""} ${d.cognome||""}`.trim().toLowerCase()===nomeCercato);
        if (dip?.nexoraUid) combo.add(`${dip.nexoraUid}|${anno}|${parseInt(mese,10)}`);
      });
    });
    if (combo.size===0) { setPresenzeMap({}); return; }
    Promise.all([...combo].map(chiave => {
      const [uid,anno,mese] = chiave.split("|");
      const docId = `${orgId}_${uid}_${anno}_${mese}`;
      return getDoc(doc(db,"presenze",docId)).then(snap => [chiave, snap.exists()?snap.data():null]);
    })).then(coppie => {
      const mappa = {};
      coppie.forEach(([chiave,dati]) => { mappa[chiave]=dati; });
      setPresenzeMap(mappa);
    });
  }, [orgId, giorniGiornaleCantiere, dipendenti]);

  // Confronta manodopera del Giornale con le presenze reali — solo un riscontro informativo
  const discrepanzeGiornalePresenze = useMemo(() => {
    const elenco = [];
    giorniGiornaleCantiere.forEach(g => {
      if (!g.data) return;
      const [anno, mese, giorno] = g.data.split("-");
      (g.manodopera||[]).forEach(r => {
        const nomeCercato = (r.nome||"").trim().toLowerCase();
        if (!nomeCercato) return;
        const dip = dipendenti.find(d => `${d.nome||""} ${d.cognome||""}`.trim().toLowerCase()===nomeCercato);
        if (!dip?.nexoraUid) return; // dipendente non collegato a un account Nexiora: non verificabile
        const chiave = `${dip.nexoraUid}|${anno}|${parseInt(mese,10)}`;
        const datiMese = presenzeMap[chiave];
        const giornoDati = datiMese?.giorni?.[parseInt(giorno,10)];
        const haTimbrature = giornoDati?.timbrature?.length > 0;
        if (!haTimbrature) {
          elenco.push({ data: g.data, nome: r.nome, ore: r.ore });
        }
      });
    });
    return elenco.sort((a,b)=>a.data.localeCompare(b.data));
  }, [giorniGiornaleCantiere, dipendenti, presenzeMap]);


  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true));
    return onSnapshot(q, snap => setUtentiNexora(snap.docs.map(d=>d.data())));
  }, [orgId]);

  // Carica impostazioni organizzazione (fonte ore)
  const [orgSettings, setOrgSettings] = useState({ fontOreEconomico: "" });
  const [orgSettingsPronte, setOrgSettingsPronte] = useState(false);
  const prevFonte = React.useRef(null);
  const [showCambioFonte, setShowCambioFonte] = React.useState(false);
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db,"organizations",orgId), snap => {
      if (snap.exists()) setOrgSettings(snap.data());
      setOrgSettingsPronte(true);
    });
  }, [orgId]);

  // Carica ore da rapportini per questo progetto
  const [oreRapportini, setOreRapportini] = useState([]);
  useEffect(() => {
    if (!orgId || !projectId) return;
    if (orgSettings.fontOreEconomico !== "rapportini") { setOreRapportini([]); return; }
    const q = query(collection(db,"rapportini"), where("orgId","==",orgId), where("projectId","==",projectId));
    return onSnapshot(q, snap => {
      const voci = [];
      snap.docs.forEach(d => {
        const r = d.data();
        // Ore per operatore (utils/oreRapportini.js): quelle scritte nel rapportino, oppure —
        // per i rapportini precedenti — la durata letta secondo l'impostazione dell'azienda.
        // Prima si cercava un campo "ore" che i rapportini non avevano: manodopera sempre zero.
        orePerOperatoreRapportino(r, orgSettings.rapportiniOrePrecedenti || "durata").forEach(({ uid: uidOperatore, nome, ore }) => {
          if (!(ore > 0)) return;
          // Le ore diventano costo solo tramite l'account (W434): mai per nome
          if (!uidOperatore) return;
          const dip = dipendenti.find(x => x.nexoraUid === uidOperatore);
          voci.push({ uid: uidOperatore, nome: nome || dip?.nome || "", ore, costoOrario: numIt(dip?.costoOrario)||0 });
        });
      });
      setOreRapportini(voci);
    });
  }, [orgId, projectId, orgSettings.fontOreEconomico, orgSettings.rapportiniOrePrecedenti, dipendenti]);

  // Ore dal diario «Le mie ore» (W403): per le persone «dal diario» le ore sulle commesse
  // vengono solo da lì. Se il diario non è leggibile (regole non ancora pubblicate) si resta
  // come prima: nessuna persona viene tolta dalla fonte dell'azienda.
  const [diario, setDiario] = useState({ blocchi: [], leggibile: false });
  useEffect(() => {
    if (!orgId || !projectId) return undefined;
    return onSnapshot(query(collection(db,"diarioOre"), where("orgId","==",orgId), where("projectId","==",projectId)),
      snap => setDiario({ blocchi: snap.docs.map(d => ({ id: d.id, ...d.data() })), leggibile: true }),
      () => setDiario({ blocchi: [], leggibile: false }));
  }, [orgId, projectId]);
  const uidDiario = useMemo(() => (diario.leggibile ? uidConDiario(dipendenti) : new Set()), [diario.leggibile, dipendenti]);
  // con un tempo in corso il costo cresce: si ricalcola ogni minuto, come il riquadro «Le mie ore» (W415)
  const [adessoDiario, setAdessoDiario] = useState(Date.now());
  const diarioInCorso = diario.blocchi.some(b => b.inCorso);
  useEffect(() => {
    if (!diarioInCorso) return undefined;
    const t = setInterval(() => setAdessoDiario(Date.now()), 60000);
    return () => clearInterval(t);
  }, [diarioInCorso]);
  const righeDiario = useMemo(() => costoDiario(diario.blocchi.filter(b => uidDiario.has(b.uid)), dipendenti, adessoDiario, numIt)
    .map(r => ({ ...r, fonte: "diario" })), [diario.blocchi, uidDiario, dipendenti, adessoDiario]);
  const totDiario = useMemo(() => righeDiario.reduce((t, r) => t + r.totale, 0), [righeDiario]);

  // Carica ore da programmazione settimanale per questo progetto
  // Legge TUTTE le programmazioni dell'org e aggrega le voci che riguardano questa commessa
  useEffect(() => {
    if (!orgId || !projectId) return;
    const q = query(collection(db,"programmazioniSettimanali"), where("organizationId","==",orgId));
    return onSnapshot(q, snap => {
      const vociProgr = [];
      snap.docs.forEach(d => {
        const data = d.data();
        const settimana = data.settimana || "";
        const lunedi = data.lunedi || "";
        Object.entries(data.celle || {}).forEach(([key, voce]) => {
          // Nuovo formato: key="uid_rowIndex_dateStr", voce=oggetto singolo
          // Vecchio formato: key="uid_dateStr", voce=array — gestiamo entrambi
          const lista = Array.isArray(voce) ? voce : (voce && typeof voce === "object" ? [voce] : []);
          lista.forEach(v => {
            if (!v.commessaId || v.commessaId !== projectId) return;
            if (!v.ore || numIt(v.ore) <= 0) return;
            const uid = key.split("_")[0];
            const dip = dipendenti.find(d => d.nexoraUid === uid) ||
                        dipendenti.find(d => d.nexoraUid === v.nexoraUid);
            vociProgr.push({
              uid,
              dipId: v.dipId || dip?.id || null,
              dipNome: v.dipNome || (dip ? (dip.nome||"")+" "+(dip.cognome||"") : "") ,
              dipIdUnico: v.dipIdUnico || dip?.idUnico || null,
              nexoraUid: v.nexoraUid || uid,
              ore: numIt(v.ore)||0,
              settimana,
            });
          });
        });
      });
      setOreProgr(vociProgr);
    });
    // anche quando arrivano i dipendenti: senza, le voci restano senza persona e costo (W415)
  }, [orgId, projectId, dipendenti]);


  // Rileva cambio fonte ore
  React.useEffect(()=>{
    const fonte = orgSettings.fontOreEconomico;
    if(!fonte) return;
    if(prevFonte.current !== null && prevFonte.current !== fonte){
      setShowCambioFonte(true);
    }
    prevFonte.current = fonte;
  },[orgSettings.fontOreEconomico]);

  // Carica budget
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db,"budgetCommessa"), where("projectId","==",projectId), where("organizationId","==",orgId));
    return onSnapshot(q, snap => {
      setBudgetDoppi(snap.size > 1 ? snap.size : 0);
      if (!snap.empty) {
        const d = snap.docs[0]; // il primo per identificativo: lo stesso che usano tutte le altre viste (budgetPrincipale)
        setBudget({id:d.id,...d.data()});
        setBudgetForm({
          importoCommessa: d.data().importoCommessa||"",
          costoPersonalePrev: d.data().costoPersonalePrev||"",
          costoMaterialiPrev: d.data().costoMaterialiPrev||"",
        });
      }
    });
  }, [projectId, orgId]);

  // Varianti e lavori extra eseguiti (scheda "Varianti e lavori extra"): il loro riepilogo
  // si somma all'importo contrattuale → totale commessa, base di ricavi e margine.
  const [varianti, setVarianti] = useState([]);
  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(query(collection(db,"attivitaExtra"), where("projectId","==",projectId), where("orgId","==",orgId)),
      snap => setVarianti(snap.docs.map(d=>d.data())));
  }, [projectId, orgId]);
  const rVarianti = useMemo(() => riepilogoVarianti(varianti), [varianti]);
  // Se il riepilogo salvato sul budget non coincide (voci di prima di ottobre 2026, o
  // modifiche fatte da un'altra schermata) lo si riallinea, così le altre viste tornano.
  useEffect(() => {
    if (!projectId || !orgId || rVarianti.n === 0 && !budget?.variantiCalcolate) return;
    if (budget && stessoRiepilogo(budget.variantiCalcolate, rVarianti)) return;
    const t = setTimeout(() => {
      sincronizzaVariantiSuBudget(projectId, orgId).catch(err => console.warn("Varianti non riallineate sul budget:", err));
    }, 3000);
    return () => clearTimeout(t);
  }, [projectId, orgId, rVarianti, budget]);

  // Suggerimento budget da Offerta Finalizzata collegata o Computo Metrico/SAL — priorità al
  // SAL quando c'è, perché di solito nasce più avanti nel tempo con numeri più affinati.
  // Non scrive mai da sola: propone soltanto, la scelta resta sempre dell'utente.
  useEffect(() => {
    if (!projectId || !orgId) { setSuggerimentoBudget(null); return; }
    (async () => {
      const salSnap = await getDocs(query(collection(db,"computiSAL"), where("projectId","==",projectId), where("orgId","==",orgId)));
      let importoSuggerito = null, fonte = null;
      if (!salSnap.empty) {
        const s = salSnap.docs[0].data();
        if ((s.periodi||[]).length>0) {
          const dati = calcolaSAL(s.righe||[], s.periodi||[], s.fatturato||0);
          if (dati.totaleGenerale>0) { importoSuggerito = dati.totaleGenerale; fonte = "Computo Metrico SAL"; }
        }
      }
      if (importoSuggerito===null) {
        const offSnap = await getDocs(query(collection(db,"elaborazioniOfferte"), where("projectIdCollegato","==",projectId), where("orgId","==",orgId), where("stato","==","finalizzata")));
        if (!offSnap.empty) {
          const totali = calcolaTotaliEO(offSnap.docs[0].data());
          if (totali.totaleGenerale>0) { importoSuggerito = totali.totaleGenerale; fonte = "Offerta Finalizzata"; }
        }
      }
      if (importoSuggerito===null) { setSuggerimentoBudget(null); return; }
      const attuale = parseValutaItaliana(budget?.importoCommessa||0);
      if (Math.abs(attuale-importoSuggerito) < 0.5) { setSuggerimentoBudget(null); return; } // già coincide
      setSuggerimentoBudget({ importo: importoSuggerito, fonte, budgetVuoto: attuale<=0 });
    })();
  }, [projectId, orgId, budget?.importoCommessa]);

  const accettaSuggerimentoBudget = async () => {
    if (!suggerimentoBudget) return;
    setSavingBudget(true);
    try {
      const dati = { importoCommessa: suggerimentoBudget.importo, projectId, organizationId: orgId,
        costoPersonalePrev: budget?.costoPersonalePrev||"", costoMaterialiPrev: budget?.costoMaterialiPrev||"",
        updatedAt: serverTimestamp() };
      if (budget?.id) await updateDoc(doc(db,"budgetCommessa",budget.id), dati);
      else await setDoc(doc(db,"budgetCommessa","budget_"+projectId), {...dati, createdAt: serverTimestamp()}, { merge: true }); // id fisso: mai due budget per la stessa commessa
      addToast(`Budget aggiornato da ${suggerimentoBudget.fonte} ✅`,"success");
    } catch (err) {
      console.error("Errore accettazione suggerimento budget:", err);
      addToast("Errore nell'aggiornare il budget: "+(err?.message||"errore sconosciuto"),"error");
    } finally {
      setSavingBudget(false);
    }
  };

  // ─── CALCOLI AUTOMATICI (useMemo = real-time al cambio di movimenti) ──
  // Giornale di Cantiere raggruppato per mese/anno — SOLO INFORMATIVO, non entra mai nei calcoli economici:
  // i costi restano calcolati esclusivamente dalla fonte ore impostata (Programmazione/Rapportini/Presenze).
  const riepilogoGiornaleCantiere = useMemo(() => {
    const mappa = {};
    giorniGiornaleCantiere.forEach(g => {
      if (!g.data) return;
      const [anno, mese] = g.data.split("-");
      const chiave = `${anno}-${mese}`;
      if (!mappa[chiave]) mappa[chiave] = { anno, mese, oreTotali: 0, numGiornali: 0 };
      mappa[chiave].numGiornali += 1;
      (g.manodopera||[]).forEach(r => { mappa[chiave].oreTotali += numIt(r.ore) || 0; });
    });
    return Object.values(mappa).sort((a,b) => `${b.anno}-${b.mese}`.localeCompare(`${a.anno}-${a.mese}`));
  }, [giorniGiornaleCantiere]);

  // Aggregazione ore — usa programmazione, rapportini o presenze in base alle impostazioni org
  const orePerDipendente = useMemo(() => {
    const fonte = orgSettings.fontOreEconomico;
    const mappa = {};

    // le persone «dal diario» hanno le loro righe dal diario (W403)
    const righeD = righeDiario.map(r => ({ nome: `${r.nome} · ⏱️ Le mie ore`, ore: r.ore, costoOrario: r.costoOrario, totale: r.totale, fonte: "diario" }));
    if (fonte === "rapportini") {
      oreRapportini.filter(v => !uidDiario.has(v.uid)).forEach(v => {
        const key = v.nome || "sconosciuto";
        if (!mappa[key]) mappa[key] = { nome: v.nome, ore: 0, costoOrario: v.costoOrario, totale: 0, fonte: "rapportini" };
        mappa[key].ore += v.ore;
        mappa[key].totale += v.ore * v.costoOrario;
      });
      return [...Object.values(mappa), ...righeD];
    }

    if (fonte === "presenze") {
      // Legge i movimenti economici già creati dall'approvazione cartellino
      // (categoriaId="manodopera" e presenzeRef presente)
      movimenti.filter(m => m.categoriaId === "manodopera" && m.presenzeRef).forEach(m => {
        const nomeDip = m.dati?.dipendente || "Sconosciuto";
        const key = nomeDip;
        const ore = numIt(m.ore) || (numIt(m.importo) / Math.max(1, numIt(m.dati?.costoOrario)||1));
        const costoOrario = ore > 0 ? Math.round((numIt(m.importo)/ore)*100)/100 : 0;
        if (!mappa[key]) mappa[key] = { nome: nomeDip, ore: 0, costoOrario, totale: 0, fonte: "presenze" };
        mappa[key].ore += ore;
        mappa[key].totale += numIt(m.importo) || 0;
      });
      return [...Object.values(mappa), ...righeD];
    }

    // Fonte programmazione (default)
    oreProgr.filter(v => !uidDiario.has(v.nexoraUid) && !uidDiario.has(v.uid)).forEach(v => {
      const dip = v.nexoraUid ? dipendenti.find(d => d.nexoraUid === v.nexoraUid) : null;
      const mapKey = v.nexoraUid || v.dipId || v.dipNome || "sconosciuto";
      const nome = dip
        ? `${dip.nome||""} ${dip.cognome||""}`.trim()
        : (v.dipNome?.trim() || v.uid || "Sconosciuto");
      const costoOrario = numIt(dip?.costoOrario)||0;
      if (!mappa[mapKey]) mappa[mapKey] = { nome, ore:0, costoOrario, totale:0, fonte: "programmazione" };
      mappa[mapKey].ore += v.ore;
      mappa[mapKey].totale += v.ore * costoOrario;
    });
    return [...Object.values(mappa), ...righeD];
  }, [oreProgr, oreRapportini, movimenti, orgSettings.fontOreEconomico, dipendenti, righeDiario, uidDiario]);

  // Persone con ore reali ma nessun costo orario collegato — le ore restano visibili nella
  // tabella qui sotto (fonte Programmazione/Rapportini calcola sempre in tempo reale, non
  // c'è un passaggio da "bloccare" come per Presenze), ma non contribuiscono al totale e
  // vanno segnalate in modo che non passino inosservate — stesso principio di Presenze.
  const dipendentiSenzaCosto = useMemo(() =>
    orePerDipendente.filter(d => d.ore > 0 && !(d.costoOrario > 0)),
    [orePerDipendente]);

  const totOreProgrammazione = useMemo(() =>
    orePerDipendente.reduce((s,d)=>s+d.totale,0), [orePerDipendente]);

  // Costo live dei noleggi mezzi d'opera — ricalcolato ad ogni render (nessuna scrittura
  // periodica su Firestore, nessun bisogno di funzioni schedulate): se un noleggio è ancora
  // attivo il costo cresce da solo confrontando la data di inizio con oggi; se è concluso
  // resta bloccato sulla data di chiusura reale.
  const totaleNoleggiMezziOpera = useMemo(() =>
    noleggiMezziOpera.reduce((s,n) => s + costoNoleggio(n), 0), [noleggiMezziOpera]);

  // W453: definiti qui perché il riepilogo li usa per IL calcolo comune dei totali
  const costiCalcolatiAttuali = useMemo(() => ({
    manodopera: Math.round((orgSettings.fontOreEconomico === "presenze" ? 0 : totOreProgrammazione - totDiario) * 100) / 100,
    diario: Math.round((totDiario || 0) * 100) / 100,
    noleggi: Math.round((totaleNoleggiMezziOpera || 0) * 100) / 100,
    fonteOre: orgSettings.fontOreEconomico || "programmazione",
  }), [orgSettings.fontOreEconomico, totOreProgrammazione, totaleNoleggiMezziOpera, totDiario]);

  const riepilogo = useMemo(() => {
    const totPerCategoria = {};
    tutteLeCategorie.forEach(c => { totPerCategoria[c.id] = 0; });
    totPerCategoria["noleggi_mezzi_opera"] = totaleNoleggiMezziOpera;
    // Aggiungi ore al totale personale
    // Fonte presenze: già nei movimenti (presenzeRef), non sommare totOreProgrammazione
    const fontaPresenze = orgSettings.fontOreEconomico === "presenze";
    if (!fontaPresenze) {
      // comprende anche le ore del diario (righe «Le mie ore» di orePerDipendente)
      totPerCategoria["personale"] = (totPerCategoria["personale"]||0) + totOreProgrammazione;
    } else {
      // fonte Presenze: il cartellino è già nei movimenti; il diario si aggiunge qui (W403)
      totPerCategoria["personale"] = (totPerCategoria["personale"]||0) + totDiario;
    }
    let ricaviMovimenti = 0;
    movimenti.forEach(m => {
      // Ricavi registrati come movimento (es. interventi di manutenzione a tariffa): sono
      // RICAVI. Prima finivano in questo stesso ciclo e venivano sommati ai costi.
      if (eRicavo(m)) { ricaviMovimenti += numIt(m.importo); return; }
      // Movimenti nati dai cartellini con una fonte ore diversa da "presenze" (es. fonte
      // cambiata dopo): la manodopera arriva già da programmazione/rapportini, contarli
      // anche qui la sommerebbe due volte.
      if (!fontaPresenze && m.presenzeRef) return;
      // Fonte presenze: salta i movimenti da presenze nella categoria personale
      // perché vengono già aggregati in orePerDipendente e mostrati nel Blocco A
      // ma NON nel totale (evita doppio conteggio)
      if (fontaPresenze && m.categoriaId === "manodopera" && m.presenzeRef) {
        // Includi nel totale personale — ma solo una volta
        totPerCategoria["personale"] = (totPerCategoria["personale"]||0) + (numIt(m.importo)||0);
        return;
      }
      if (totPerCategoria[m.categoriaId] !== undefined) {
        totPerCategoria[m.categoriaId] += numIt(m.importo)||0;
      } else {
        totPerCategoria[m.categoriaId] = (totPerCategoria[m.categoriaId]||0) + (numIt(m.importo)||0);
      }
    });

    // Costi materiali dagli Ordini di Acquisto avviati — sommati alla categoria "materiali"
    // Non usiamo una chiave separata per evitare doppio conteggio nel totale
    const totNfMateriali = costiMateriali.reduce((s, nf) => {
      let imp = 0;
      if (typeof nf.importo === "number") imp = nf.importo;
      else if (typeof nf.importo === "string") imp = parseFloat(nf.importo.replace(/\./g,"").replace(",",".")) || 0;
      return s + imp;
    }, 0);
    // Manteniamo nf_materiali separato SOLO per la visualizzazione nel KPI
    // ma lo escludiamo dal totaleCosti per evitare di contare due volte
    // (è già compreso nei movimenti "materiali" se inseriti manualmente, altrimenti è aggiuntivo)
    // NOTA: NF sono acquisti esterni formali — non devono coincidere con movimenti manuali
    // Se l'utente segue il flusso corretto (NF → non inserisce manuale), non c'è doppio conteggio
    totPerCategoria["nf_materiali"] = totNfMateriali;

    // Il totaleCosti include: tutte le categorie + NF materiali
    // Per sicurezza, la categoria "materiali" manuale e NF sono mostrate separatamente
    // W453: i totali vengono da IL calcolo comune (utils/economiaCommessa.js, totaliCommessa), lo
    // stesso di report, Relazione finale, Cabina di Regia e viste con più commesse. Le voci qui
    // sopra sono il dettaglio per categoria e devono sommare allo stesso totale.
    const tot = totaliCommessa({ budget, movimenti, costoOda: totNfMateriali, fontOre: costiCalcolatiAttuali.fonteOre,
      costiCalcolati: costiCalcolatiAttuali, totaleVarianti: rVarianti.totale });
    const sommaVoci = Object.values(totPerCategoria).reduce((a,b)=>a+b,0);
    if (Math.abs(sommaVoci - tot.costi) > 0.01) console.warn("Controllo Economico: le voci non sommano al totale", sommaVoci, tot.costi);
    const totaleCosti = tot.costi;
    const importoCommessa = tot.contratto;
    // Totale commessa = importo contrattuale + varianti accettate (il budget originale non cambia).
    const totaleVarianti = tot.varianti;
    const totaleCommessa = importoCommessa + totaleVarianti;
    const ricaviTotali = tot.ricavi;
    const margine = ricaviTotali - totaleCosti;
    const marginePercent = ricaviTotali > 0 ? (margine/ricaviTotali)*100 : 0;
    // Costo previsto = budget (personale + materiali previsti) + costo materiali previsto delle
    // varianti accettate o eseguite (utils/varianti.js). Non entra mai nei costi reali: lì
    // arriverà con l'ordine vero, e conterebbe due volte.
    // Formula unica in utils/economiaCommessa.js (la stessa che Insight somma sulle commesse aperte);
    // qui il costo previsto delle varianti arriva dal riepilogo appena calcolato.
    const mp = marginePrevistoCommessa({ budget, ricavi: ricaviTotali, costiReali: totaleCosti, costoPrevistoVarianti: rVarianti.costoPrevisto || 0 });
    const budgetPrevistoBase = mp.costoPrevistoBase;
    const costoPrevistoVarianti = mp.costoPrevistoVarianti;
    const budgetPrevisto = mp.costoPrevisto;
    const scostamento = budgetPrevisto > 0 ? totaleCosti - budgetPrevisto : null;
    const marginePrevisto = mp.marginePrevisto;
    return { totPerCategoria, totaleCosti, importoCommessa, totaleVarianti, totaleCommessa, ricaviMovimenti, ricaviTotali, margine, marginePercent, budgetPrevisto, budgetPrevistoBase, costoPrevistoVarianti, marginePrevisto, scostamento };
  }, [movimenti, budget, tutteLeCategorie, costiMateriali, totOreProgrammazione, orgSettings, totaleNoleggiMezziOpera, rVarianti, totDiario, costiCalcolatiAttuali]);

  const semaforo = semaforoEconomico(riepilogo.marginePercent);

  // ── Costi calcolati al momento, salvati per le altre schermate (ott 2026) ──────────
  // Manodopera da Programmazione/Rapportini e noleggi in corso non sono movimenti: li
  // calcola solo questa pagina. Perché Cabina di Regia, Insight, Centro Operativo, Report e le
  // altre viste mostrino lo STESSO margine, il risultato si salva sul budget della commessa
  // (costiCalcolati) e utils/economiaCommessa.js lo somma. Si scrive solo quando i valori
  // cambiano e restano stabili qualche secondo (dati tutti caricati), e solo se il budget
  // della commessa esiste già (mai creare un secondo documento budget).
  // manodopera = ore dalla fonte dell'azienda (senza il diario); diario = ore «Le mie ore»,
  // che contano con qualunque fonte (utils/economiaCommessa.js, costiCalcolatiDaBudget)

  useEffect(() => {
    if (!orgSettingsPronte || !budget?.id) return;
    const salvati = budget.costiCalcolati;
    const c = costiCalcolatiAttuali;
    if (salvati && salvati.fonteOre === c.fonteOre && Math.abs((salvati.manodopera||0) - c.manodopera) < 0.5 && Math.abs((salvati.diario||0) - c.diario) < 0.5 && Math.abs((salvati.noleggi||0) - c.noleggi) < 0.5) return;
    const t = setTimeout(() => {
      updateDoc(doc(db,"budgetCommessa",budget.id), { costiCalcolati: { ...c, aggiornatiIl: serverTimestamp() } })
        .catch(err => console.warn("Costi calcolati non salvati:", err));
    }, 3000);
    return () => clearTimeout(t);
  }, [costiCalcolatiAttuali, budget?.id, budget?.costiCalcolati, orgSettingsPronte]);

  // ── Importi inseriti a mano letti in modo sbagliato prima dell'ottobre 2026 ──────────
  // Il modulo leggeva gli importi con parseFloat: "1.250,00" diventava 1,25 e "12,50"
  // diventava 12. I dati scritti dall'utente sono conservati: qui si ricalcola l'importo
  // con la lettura corretta e si segnalano i movimenti che non tornano, da correggere con
  // un clic (resta traccia nel registro modifiche).
  const movimentiDaCorreggere = useMemo(() => movimenti.filter(m => {
    if (m.tipo==="ricavo" || origineAutomatica(m) || !m.dati) return false; // le voci automatiche si correggono dove nascono
    const cat = tutteLeCategorie.find(c => c.id===m.categoriaId);
    if (!cat || cat.readOnly || typeof cat.calcola!=="function") return false;
    const giusto = cat.calcola(m.dati);
    return giusto > 0 && Math.abs(giusto - numIt(m.importo)) > 0.009;
  }), [movimenti, tutteLeCategorie]);
  const [correggendo, setCorreggendo] = useState(false);

  // ── Note spese contabilizzate due volte (prima di ottobre 2026) ───────────────────────
  // Un'approvazione ripetuta (doppio clic, due amministratori insieme, rete di sicurezza della
  // pagina Nota spese) poteva creare lo stesso costo due volte. Da ottobre 2026 non succede
  // più (id fisso per nota e giorno); qui si trovano gli eventuali doppioni già esistenti:
  // stessa nota, stessa descrizione (che contiene il giorno), stesso importo.
  const doppioniNoteSpese = useMemo(() => {
    const gruppi = {};
    movimenti.filter(m => m.notaSpeseRef && m.tipo!=="ricavo").forEach(m => {
      const k = `${m.notaSpeseRef}|${m.dati?.descrizione||""}|${numIt(m.importo).toFixed(2)}`;
      (gruppi[k] = gruppi[k] || []).push(m);
    });
    const extra = [];
    Object.values(gruppi).filter(g => g.length > 1).forEach(g => {
      const tenuto = g.find(m => String(m.id).startsWith("ns_")) || g[0];
      g.filter(m => m.id !== tenuto.id).forEach(m => extra.push(m));
    });
    return extra;
  }, [movimenti]);
  const [togliendoDoppioni, setTogliendoDoppioni] = useState(false);
  const togliDoppioni = async () => {
    if (togliendoDoppioni) return;
    if (!await confirm(`Rimuovere ${doppioniNoteSpese.length} ${doppioniNoteSpese.length===1?"costo duplicato":"costi duplicati"} di note spese? Resta un solo costo per ogni giorno di nota spese.`, { type:"warning" })) return;
    setTogliendoDoppioni(true);
    try {
      for (const m of doppioniNoteSpese) {
        await deleteDoc(doc(db,"movimentiEconomici",m.id));
        await logModifica({ orgId, uid: currentUser.uid, nomeUtente: currentUser.displayName||"",
          sezione: "movimentiEconomici", refId: m.id, refNome: m.dati?.descrizione || "Nota spese",
          campo: "importo", campoLabel: "Costo di nota spese duplicato rimosso", before: String(numIt(m.importo)), after: "—" });
      }
      addToast("Doppioni rimossi", "success");
    } catch (e) { addToast(messaggioErrore(e), "error"); }
    setTogliendoDoppioni(false);
  };
  const correggiImporti = async () => {
    if (correggendo) return;
    setCorreggendo(true);
    try {
      for (const m of movimentiDaCorreggere) {
        const cat = tutteLeCategorie.find(c => c.id===m.categoriaId);
        const giusto = Math.round(cat.calcola(m.dati)*100)/100;
        await updateDoc(doc(db,"movimentiEconomici",m.id), { importo: giusto, importoCorrettoDa: numIt(m.importo), modificataDa: currentUser.uid, modificataAt: serverTimestamp() });
        await logModifica({ orgId, uid: currentUser.uid, nomeUtente: currentUser.displayName||"",
          sezione: "movimentiEconomici", refId: m.id, refNome: m.dati?.descrizione || m.categoriaLabel || "",
          campo: "importo", campoLabel: "Importo ricalcolato (lettura corretta delle cifre)", before: String(numIt(m.importo)), after: String(giusto) });
      }
      addToast(`${movimentiDaCorreggere.length} importi corretti`, "success");
    } catch (e) { addToast(messaggioErrore(e), "error"); }
    setCorreggendo(false);
  };

  const totOreTotali = useMemo(() => orePerDipendente.reduce((s,d)=>s+(d.ore||0),0), [orePerDipendente]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [apertoStorico, setApertoStorico] = useState(true);

  const salvaSnapshot = async () => {
    setSavingSnapshot(true);
    const nuovoSnapshot = {
      data: toLocalDateStr(new Date()),
      fonte: orgSettings.fontOreEconomico || "—",
      oreTotali: totOreTotali,
      costoTotale: riepilogo.totaleCosti,
    };
    try {
      if (budget?.id) {
        await updateDoc(doc(db,"budgetCommessa",budget.id), { storicoSnapshot: arrayUnion(nuovoSnapshot) });
      } else {
        await setDoc(doc(db,"budgetCommessa","budget_"+projectId), {
          projectId, organizationId: orgId, storicoSnapshot: arrayUnion(nuovoSnapshot), createdAt: serverTimestamp(),
        }, { merge: true });
      }
      addToast("Istantanea salvata ✅", "success");
    } catch(err) { addToast(messaggioErrore(err, "salvare l'istantanea"), "error"); }
    setSavingSnapshot(false);
  };

  const salvaBudget = async () => {
    setSavingBudget(true);
    const data = {
      projectId, organizationId: orgId,
      importoCommessa: parseValutaItaliana(budgetForm.importoCommessa),
      costoPersonalePrev: parseValutaItaliana(budgetForm.costoPersonalePrev),
      costoMaterialiPrev: parseValutaItaliana(budgetForm.costoMaterialiPrev),
      // Non più un campo libero: sempre uguale alla somma di personale+materiali previsti.
      // Resta comunque salvato perché Nexiora Insight lo legge per segnalare le commesse fuori budget.
      budgetPrevisto: parseValutaItaliana(budgetForm.costoPersonalePrev) + parseValutaItaliana(budgetForm.costoMaterialiPrev),
      updatedAt: serverTimestamp(),
    };
    try {
      if (budget?.id) await updateDoc(doc(db,"budgetCommessa",budget.id), data);
      else await setDoc(doc(db,"budgetCommessa","budget_"+projectId), {...data, createdAt:serverTimestamp()}, { merge: true });
      addToast("Budget salvato", "success");
      setEditBudget(false);
    } catch(err) { addToast(messaggioErrore(err, "salvare il budget"), "error"); }
    setSavingBudget(false);
  };

  const aggiungiCategoria = () => {
    if (!nuovaCategoria.label.trim()) return;
    setCategoriExtra(prev => [...prev, { id: "custom_"+Date.now(), label: nuovaCategoria.label.trim(), icon: nuovaCategoria.icon }]);
    setNuovaCategoria({ label:"", icon:"📦" });
    setShowAddCategoria(false);
  };

  return (
    <div style={{padding:24}}>

      {/* Banner cambio fonte ore */}
      {showCambioFonte && (
        <div style={{margin:"8px 16px",padding:"10px 14px",background:"#fffbeb",border:"1px solid #f59e0b",borderLeft:"4px solid #f59e0b",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>⚠️</span>
            <span style={{fontSize:12,color:"#92400e",fontWeight:600}}>
              Fonte ore cambiata — i totali sono stati ricalcolati sulla nuova fonte. Verificare la correttezza dei dati.
            </span>
          </div>
          <button onClick={()=>setShowCambioFonte(false)}
            style={{background:"none",border:"none",cursor:"pointer",color:"#92400e",fontSize:16,padding:"0 4px",fontWeight:700}}>×</button>
        </div>
      )}
      {/* Banner fonte ore non impostata — CRITICO: il totale costi è incompleto */}
      {!orgSettings.fontOreEconomico && (
        <div style={{background:"#fef2f2",border:"2px solid #dc2626",borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:22,flexShrink:0}}>🚫</span>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"#dc2626",marginBottom:2}}>
              Fonte ore manodopera non impostata — i costi di manodopera non sono inclusi nel totale
            </div>
            <div style={{fontSize:11,color:"#991b1b",lineHeight:1.5}}>
              Il totale costi e il margine mostrati non sono corretti finché non viene configurata la fonte ore.<br/>
              Vai in <strong>Impostazioni aziendali → Fonte ore</strong> e scegli: Programmazione Settimanale, Rapportini o Presenze.
            </div>
            {(isOrgAdmin||isPlatformOwner) && onNavigate && (
              <button onClick={()=>onNavigate({page:"impostazioni-org"})}
                style={{marginTop:8,padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Vai a Impostazioni aziendali →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Banner ore senza costo collegato — le ore ci sono ma non contano nel totale */}
      {dipendentiSenzaCosto.length>0 && (
        <div style={{background:"#fef2f2",border:"2px solid #dc2626",borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"flex-start",gap:10}}>
          <span style={{fontSize:22,flexShrink:0}}>🚫</span>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"#dc2626",marginBottom:4}}>
              {dipendentiSenzaCosto.length} persona/e con ore registrate ma nessun costo orario collegato — non contano nel totale
            </div>
            <div style={{fontSize:11,color:"#991b1b",lineHeight:1.5,marginBottom:6}}>
              {dipendentiSenzaCosto.map(d=>`${d.nome} (${d.ore.toFixed(1)}h)`).join(" · ")}
            </div>
            <div style={{fontSize:11,color:"#991b1b",lineHeight:1.5}}>
              <strong>Cosa fare:</strong> vai in <strong>Dipendenti &amp; Collaboratori</strong> e collega ciascuna di queste persone a un Dipendente con un Costo orario valido — oppure imposta un <strong>Costo orario base</strong> in Impostazioni Organizzazione come ripiego generale.
            </div>
            {canSee("dipendenti") && onNavigate && (
              <button onClick={()=>onNavigate({page:"dipendenti"})}
                style={{marginTop:8,padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Vai a Dipendenti &amp; Collaboratori →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── RIEPILOGO ECONOMICO ── */}
      {budgetDoppi > 0 && (
        <div role="alert" style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"8px 12px",marginBottom:10,fontSize:12,color:"#92400e"}}>
          ⚠️ Questa commessa ha {budgetDoppi} documenti di budget (un doppione nato da una versione precedente): Nexiora usa ovunque il principale, quello mostrato qui. Segnalalo all'assistenza per eliminare il doppione.
        </div>
      )}
      <div style={{background:semaforo.bg,border:`1px solid ${semaforo.colore}44`,borderRadius:16,padding:20,marginBottom:24}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <h3 style={{fontSize:15,fontWeight:800,color:"#111827",display:"flex",alignItems:"center",gap:8}}>
            {semaforo.emoji} Riepilogo Economico
            <span style={{fontSize:12,fontWeight:600,color:semaforo.colore,background:semaforo.bg,padding:"2px 10px",borderRadius:12,border:`1px solid ${semaforo.colore}44`}}>
              {semaforo.label}
            </span>
            <span style={{fontSize:11.5,fontWeight:600,color:"#78716c",background:"#fafaf9",padding:"3px 10px",borderRadius:8,border:"1px solid #e7e5e4",display:"flex",alignItems:"center",gap:5}}>
              ℹ️ Manodopera, note spese e materiali sono calcolati solo se già accettati — verificare che lo siano
            </span>
          </h3>
          {canEdit && (
            <button onClick={()=>setEditBudget(!editBudget)}
              style={{background:"none",border:"1px solid #e5e7eb",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer",color:"#6b7280",fontFamily:"'Inter',sans-serif"}}>
              {editBudget?"✓ Chiudi":"✏️ Budget"}
            </button>
          )}
        </div>

        {suggerimentoBudget && canEdit && (
          <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12.5,color:"#5b21b6",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{flex:1}}>
              💜 {suggerimentoBudget.budgetVuoto
                ? <>Trovato un importo da <strong>{suggerimentoBudget.fonte}</strong>: € {formatValutaItaliana(suggerimentoBudget.importo)} — usarlo come budget?</>
                : <>Il budget qui è € {formatValutaItaliana(budget?.importoCommessa||0)}, ma <strong>{suggerimentoBudget.fonte}</strong> dice € {formatValutaItaliana(suggerimentoBudget.importo)} — vuoi aggiornarlo?</>}
            </span>
            <button onClick={accettaSuggerimentoBudget} disabled={savingBudget}
              style={{padding:"5px 12px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",fontSize:11.5,fontWeight:700,cursor:"pointer",flexShrink:0}}>
              {savingBudget?"...":suggerimentoBudget.budgetVuoto?"Usa questo importo":"Aggiorna"}
            </button>
            <button onClick={()=>setSuggerimentoBudget(null)} style={{background:"none",border:"none",color:"#7c3aed",cursor:"pointer",fontWeight:700,fontSize:14,flexShrink:0}}>×</button>
          </div>
        )}

        {/* Form budget */}
        {editBudget && canEdit && (
          <div style={{background:"#fff",borderRadius:10,padding:16,marginBottom:16,border:"1px solid #e5e7eb"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              {[
                {key:"importoCommessa", label:"💰 Importo commessa (€)"},
                {key:"costoPersonalePrev", label:"👷 Costo personale previsto (€)"},
                {key:"costoMaterialiPrev", label:"🔧 Costo materiali previsto (€)"},
              ].map(f => (
                <div key={f.key}>
                  <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>{f.label}</label>
                  <input className="input" type="text" inputMode="decimal"
                    value={budgetForm[f.key]} onChange={e=>setBudgetForm(b=>({...b,[f.key]:e.target.value}))}
                    placeholder="Es. 45.000"/>
                </div>
              ))}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>📋 Budget costi previsto (calcolato)</label>
                <div className="input" style={{background:"#f9fafb",color:"#374151",fontWeight:600,display:"flex",alignItems:"center"}}>
                  € {formatValutaItaliana(parseValutaItaliana(budgetForm.costoPersonalePrev||0)+parseValutaItaliana(budgetForm.costoMaterialiPrev||0))}
                </div>
                <div style={{fontSize:10,color:"#9ca3af",marginTop:3}}>Somma automatica di personale + materiali previsti</div>
              </div>
            </div>
            <button onClick={salvaBudget} disabled={savingBudget}
              style={{padding:"8px 20px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {savingBudget?"Salvo...":"Salva budget"}
            </button>
          </div>
        )}

        {doppioniNoteSpese.length>0 && (
          <div style={{background:"#fff7ed",border:"1px solid #fdba74",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#9a3412",lineHeight:1.5}}>
            <strong>⚠️ {doppioniNoteSpese.length} {doppioniNoteSpese.length===1?"costo di nota spese risulta contato due volte":"costi di note spese risultano contati due volte"}</strong>
            {" "}(stessa nota, stesso giorno, stesso importo — € {formatValutaItaliana(doppioniNoteSpese.reduce((t,m)=>t+numIt(m.importo),0))} in più nei costi).{" "}
            {canEdit
              ? <button onClick={togliDoppioni} disabled={togliendoDoppioni} style={{marginLeft:6,padding:"4px 12px",background:"#ea580c",color:"#fff",border:"none",borderRadius:7,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{togliendoDoppioni?"Rimozione…":"Rimuovi i doppioni"}</button>
              : <span>Un amministratore può rimuoverli da questa pagina.</span>}
          </div>
        )}

        {movimentiDaCorreggere.length>0 && (
          <div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#991b1b",lineHeight:1.5}}>
            <strong>⚠️ {movimentiDaCorreggere.length} {movimentiDaCorreggere.length===1?"costo inserito a mano risulta registrato":"costi inseriti a mano risultano registrati"} con un importo sbagliato</strong>
            {" "}(cifre con punto o virgola lette male prima dell'aggiornamento di ottobre 2026):
            <ul style={{margin:"6px 0 6px 18px"}}>
              {movimentiDaCorreggere.slice(0,8).map(m=>{
                const cat = tutteLeCategorie.find(c=>c.id===m.categoriaId);
                return <li key={m.id}>{cat?.label}: {m.dati?.descrizione||m.dati?.nome||"—"} — registrato € {formatValutaItaliana(numIt(m.importo))}, corretto € {formatValutaItaliana(cat.calcola(m.dati))}</li>;
              })}
              {movimentiDaCorreggere.length>8 && <li>… e altri {movimentiDaCorreggere.length-8}</li>}
            </ul>
            {canEdit
              ? <button onClick={correggiImporti} disabled={correggendo} style={{padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{correggendo?"Correzione…":"Correggi gli importi"}</button>
              : <span>Un amministratore può correggerli da questa pagina.</span>}
          </div>
        )}

        {/* KPI cards */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8}}>
          {[
            { label:"Importo commessa",    val: riepilogo.importoCommessa>0 ? `€ ${formatValutaItaliana(riepilogo.importoCommessa)}` : "—", colore:"#111827",
              nota: riepilogo.ricaviMovimenti>0 ? `+ ricavi da interventi € ${formatValutaItaliana(riepilogo.ricaviMovimenti)}` : "budget originale" },
            // Varianti: subito a destra del budget. Vuoto se non ce ne sono, avviso se eseguite ma non valorizzate.
            { label:"➕ Varianti",
              val: rVarianti.n===0 ? "—" : `€ ${formatValutaItaliana(rVarianti.totale)}`,
              colore: rVarianti.n===0 ? "#9ca3af" : "#7c3aed",
              bordo: (rVarianti.nDaValorizzare>0 || rVarianti.inAttesa>0) ? "#fb923c" : null,
              click: onApriVarianti || null,
              nota: rVarianti.n===0 ? "nessuna variante"
                : [rVarianti.totale>0 || rVarianti.inAttesa>0 ? "accettate" : null,
                   rVarianti.daFatturare>0 ? `da fatturare € ${formatValutaItaliana(rVarianti.daFatturare)}` : null,
                   rVarianti.inAttesa>0 ? `⏳ in attesa di accettazione € ${formatValutaItaliana(rVarianti.inAttesa)} (fuori dal margine)` : null,
                   rVarianti.nDaValorizzare>0 ? `⚠️ ${rVarianti.nDaValorizzare} senza importo` : null].filter(Boolean).join(" · ") || "tutte fatturate",
              notaColore: (rVarianti.nDaValorizzare>0 || rVarianti.inAttesa>0) ? "#c2410c" : null },
            ...(rVarianti.n>0 ? [{ label:"Totale commessa", val: `€ ${formatValutaItaliana(riepilogo.totaleCommessa)}`, colore:"#1e1b4b", bold:true, nota:"contratto + varianti accettate" }] : []),
            // Materiali = ordini di acquisto + materiali inseriti a mano (prima il riquadro
            // mostrava solo gli ODA, anche se i manuali erano già nel totale costi e nel margine)
            { label:"🔧 Materiali",  val: `€ ${formatValutaItaliana((riepilogo.totPerCategoria?.nf_materiali||0)+(riepilogo.totPerCategoria?.materiali||0))}`,
              colore: ((riepilogo.totPerCategoria?.nf_materiali||0)+(riepilogo.totPerCategoria?.materiali||0))>0?"#0891b2":"#9ca3af",
              nota: ((riepilogo.totPerCategoria?.nf_materiali||0)>0||(riepilogo.totPerCategoria?.materiali||0)>0)
                ? `ordini € ${formatValutaItaliana(riepilogo.totPerCategoria?.nf_materiali||0)} · a mano € ${formatValutaItaliana(riepilogo.totPerCategoria?.materiali||0)} · IVA esclusa`
                : null },
            { label:"👷 Manodopera",  val: `€ ${formatValutaItaliana(
                riepilogo.totPerCategoria?.personale||0
              )}`, colore:"#7c3aed" },
            { label:"🧾 Note spese",         val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.nota_spese||0)}`,   colore:"#0891b2" },
            { label:"🏗️ Subappaltatori",      val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.subappaltatore||0)}`, colore:"#059669" },
            { label:"🚧 Noleggi mezzi d'opera", val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.noleggi_mezzi_opera||0)}`, colore:"#c2410c" },
            { label:"🎓 Professionisti", val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.professionisti||0)}`, colore:"#8b5cf6" },
            { label:"📋 Pratiche",               val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.pratiche||0)}`,       colore:"#0e7490" },
            { label:"📦 Varie",                  val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.varie||0)}`,          colore:"#64748b" },
            { label:"🚗 Trasporti",              val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.trasporti||0)}`,      colore:"#059669" },
            { label:"🏨 Vitto e alloggio",       val: `€ ${formatValutaItaliana(riepilogo.totPerCategoria?.vitto||0)}`,          colore:"#d97706" },
            { label:"Totale costi",         val: `€ ${formatValutaItaliana(riepilogo.totaleCosti)}`, colore:"#dc2626", bold:true },
            { label:"Margine attuale",      val: riepilogo.ricaviTotali>0 ? `€ ${formatValutaItaliana(riepilogo.margine)}` : "—", colore: riepilogo.margine>=0?"#059669":"#dc2626",
              nota: riepilogo.ricaviTotali>0 ? "ricavi accettati − costi sostenuti" : null },
            ...(riepilogo.marginePrevisto!==null && riepilogo.ricaviTotali>0 ? [{ label:"Margine previsto", val: `€ ${formatValutaItaliana(riepilogo.marginePrevisto)}`,
              colore: riepilogo.marginePrevisto>=0?"#059669":"#dc2626",
              nota: `costo previsto € ${formatValutaItaliana(riepilogo.budgetPrevisto)}${riepilogo.costoPrevistoVarianti>0 ? ` (di cui varianti € ${formatValutaItaliana(riepilogo.costoPrevistoVarianti)})` : ""}${riepilogo.totaleCosti>riepilogo.budgetPrevisto ? " · superato dai costi reali" : ""}` }] : []),
            { label:"Redditività",          val: riepilogo.ricaviTotali>0 ? `${riepilogo.marginePercent.toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%` : "—", colore:semaforo.colore },
            ...(riepilogo.scostamento!==null && riepilogo.totaleCosti>0 ? [{ label:"Scostamento budget", val: riepilogo.scostamento>=0 ? `+€ ${formatValutaItaliana(riepilogo.scostamento)}` : `€ ${formatValutaItaliana(Math.abs(riepilogo.scostamento))}`, colore: riepilogo.scostamento<=0?"#059669":"#dc2626" }] : []),
          ].map(k => (
            <div key={k.label} onClick={k.click||undefined} title={k.click?"Apri Varianti e lavori extra":undefined}
              style={{background:"#fff",borderRadius:8,padding:"10px 10px",border:`1px solid ${k.bordo||"#e5e7eb"}`,cursor:k.click?"pointer":"default"}}>
              <div style={{fontSize:10,color:"#9ca3af",marginBottom:3,lineHeight:1.2}}>{k.label}</div>
              <div style={{fontSize:13,fontWeight:800,color:k.colore,lineHeight:1.2}}>{k.val}</div>
              {/* Dettaglio sotto il valore (es. Materiali: ordini + a mano). Prima la nota era
                  preparata ma non veniva mai mostrata. */}
              {k.nota && <div style={{fontSize:9.5,color:k.notaColore||"#6b7280",marginTop:3,lineHeight:1.3}}>{k.nota}</div>}
            </div>
          ))}
        </div>

        {/* Istantanea valori attuali (ore totali + costo) — utile prima di un eventuale cambio fonte ore */}
        <div style={{marginTop:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <button onClick={salvaSnapshot} disabled={savingSnapshot}
            style={{padding:"6px 14px",border:"1px solid #7c3aed",borderRadius:8,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            {savingSnapshot ? "Salvataggio..." : "📸 Salva istantanea valori attuali"}
          </button>
          <span style={{fontSize:11,color:"#9ca3af"}}>
            Ore totali attuali: <strong style={{color:"#374151"}}>{totOreTotali.toFixed(1)}</strong> · Costo totale attuale: <strong style={{color:"#374151"}}>€ {formatValutaItaliana(riepilogo.totaleCosti)}</strong>
          </span>
        </div>
        <div style={{marginTop:8,padding:"8px 12px",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,fontSize:12,color:"#5b21b6",fontWeight:600,lineHeight:1.5}}>
          ℹ️ Salva un'istantanea solo se stai per cambiare la fonte delle ore lavorate nelle Impostazioni Organizzazione, così da conservare i valori calcolati con la fonte attuale. Se non prevedi di cambiarla, non occorre fare nulla.
        </div>

        {budget?.storicoSnapshot?.length > 0 && (
          <div style={{marginTop:10,background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:"10px 14px"}}>
            <div onClick={()=>setApertoStorico(a=>!a)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",marginBottom: apertoStorico?6:0}}>
              <div style={{fontSize:11,fontWeight:700,color:"#6b7280"}}>📋 Istantanee salvate (sola consultazione)</div>
              {apertoStorico ? <ChevronUp size={14} color="#9ca3af"/> : <ChevronDown size={14} color="#9ca3af"/>}
            </div>
            {apertoStorico && (() => {
              const inverso = [...budget.storicoSnapshot].reverse();
              const idxUltimaPrecedente = inverso.findIndex(s => s.fonte !== (orgSettings.fontOreEconomico || "—"));
              return inverso.map((s,i)=>{
                const fontePrecedente = i === idxUltimaPrecedente;
                return (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,fontSize:11,color:"#374151",padding:"4px 0",borderBottom: i<budget.storicoSnapshot.length-1 ? "1px solid #f3f4f6" : "none"}}>
                    <span style={{color:"#9ca3af",minWidth:80}}>{new Date(s.data+"T00:00:00").toLocaleDateString("it-IT")}</span>
                    <span>Fonte: <strong>{s.fonte}</strong></span>
                    <span>Ore: <strong>{s.oreTotali.toFixed(1)}</strong></span>
                    <span>Costo: <strong>€ {formatValutaItaliana(s.costoTotale)}</strong></span>
                    {fontePrecedente && (
                      <span style={{fontSize:10,fontWeight:700,color:"#d97706",background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:8,padding:"1px 8px"}}>
                        📌 Fonte precedente
                      </span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {riepilogo.totaleCosti > 0 && (
          <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${semaforo.colore}33`,display:"flex",gap:16,flexWrap:"wrap"}}>
            {/* Materiali = inseriti a mano + ordini di acquisto, come nel riquadro in alto (prima
                qui comparivano solo quelli a mano e i due numeri non coincidevano) */}
            {tutteLeCategorie.map(c=>({ c, v: (riepilogo.totPerCategoria[c.id]||0) + (c.id==="materiali" ? (riepilogo.totPerCategoria.nf_materiali||0) : 0) }))
              .filter(x=>x.v>0).map(({c,v})=>(
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:5}}>
                <span>{c.icon}</span>
                <span style={{fontSize:12,color:"#6b7280"}}>{c.label}:</span>
                <span style={{fontSize:12,fontWeight:700,color:c.colore}}>€ {formatValutaItaliana(v)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── GIORNALE DI CANTIERE — SOLO INFORMATIVO, non incluso nei costi ── */}
      {riepilogoGiornaleCantiere.length > 0 && (
        <div style={{background:"#fff", borderLeft:"1.5px solid #b4530955", borderRight:"1.5px solid #b4530955", borderBottom:"1.5px solid #b4530955", borderTop:"4px solid #b45309", borderRadius:16, padding:20, marginBottom:24, boxShadow:"0 4px 14px rgba(17,24,39,.07)"}}>
          <h3 style={{fontSize:14, fontWeight:800, color:"#b45309", display:"flex", alignItems:"center", gap:8, marginBottom:4}}>
            📓 Ore da Giornale di Cantiere
          </h3>
          <p style={{fontSize:11, color:"#9ca3af", marginBottom:14, lineHeight:1.5}}>
            Dato puramente informativo, per confronto — <strong>non è incluso nel calcolo dei costi</strong> qui sopra.
            I costi di manodopera restano calcolati solo dalla fonte ore impostata (Programmazione/Rapportini/Presenze),
            perché il Giornale di Cantiere può contenere dati non sempre precisi al minuto.
          </p>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:10}}>
            {riepilogoGiornaleCantiere.map(r => (
              <div key={`${r.anno}-${r.mese}`} style={{background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"10px 12px"}}>
                <div style={{fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:4}}>
                  {["","Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"][parseInt(r.mese)]} {r.anno}
                </div>
                <div style={{fontSize:18, fontWeight:800, color:"#374151"}}>{r.oreTotali}h</div>
                <div style={{fontSize:11, color:"#9ca3af"}}>{r.numGiornali} giornal{r.numGiornali===1?"e":"i"} compilat{r.numGiornali===1?"o":"i"}</div>
              </div>
            ))}
          </div>
          {discrepanzeGiornalePresenze.length > 0 && (
            <div style={{marginTop:14, paddingTop:14, borderTop:"1px solid #e5e7eb"}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer"}}
                onClick={()=>setMostraDiscrepanzeGP(v=>!v)}>
                <span style={{fontSize:12, fontWeight:700, color:"#d97706"}}>
                  ⚠️ {discrepanzeGiornalePresenze.length} giornat{discrepanzeGiornalePresenze.length===1?"a":"e"} con manodopera nel Giornale senza timbratura corrispondente — verificare
                </span>
                <span style={{fontSize:11, color:"#9ca3af"}}>{mostraDiscrepanzeGP?"nascondi ▲":"mostra ▼"}</span>
              </div>
              {mostraDiscrepanzeGP && (
                <div style={{marginTop:8, display:"flex", flexDirection:"column", gap:4}}>
                  {discrepanzeGiornalePresenze.map((d,i) => (
                    <div key={i} style={{fontSize:11, color:"#92400e", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"5px 10px"}}>
                      {new Date(d.data+"T00:00:00").toLocaleDateString("it-IT")} — <strong>{d.nome}</strong>{d.ore?` (${d.ore}h nel Giornale)`:""}
                    </div>
                  ))}
                </div>
              )}
              <div style={{fontSize:10, color:"#9ca3af", marginTop:6}}>
                Confronto solo per dipendenti collegati a un account Nexiora — utile per intercettare errori di trascrizione prima della fatturazione, non è una verifica automatica definitiva.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CONFRONTO PREVENTIVO vs CONSUNTIVO + PREVISIONE ── */}
      {(riepilogo.budgetPrevisto > 0 || riepilogo.importoCommessa > 0) && riepilogo.totaleCosti > 0 && (() => {
        // Calcola % consumo budget
        const pctBudget = riepilogo.budgetPrevisto > 0 ? Math.round(riepilogo.totaleCosti / riepilogo.budgetPrevisto * 100) : null;
        // Previsione fine commessa basata sul ritmo di spesa
        const p = projectData || {};
        const dataInizio = p.dataInizio ? new Date(p.dataInizio) : null;
        const dataFine = p.dataFine ? new Date(p.dataFine) : null;
        const oggi = new Date();
        let previsioneEl = null;
        if (dataInizio && dataFine && riepilogo.totaleCosti > 0 && riepilogo.budgetPrevisto > 0) {
          const durataGiorni = (dataFine - dataInizio) / (1000*60*60*24);
          const giorniPassati = Math.max(1, (oggi - dataInizio) / (1000*60*60*24));
          const ritmoGiornaliero = riepilogo.totaleCosti / giorniPassati;
          const costoStimato = ritmoGiornaliero * durataGiorni;
          const margineStimato = riepilogo.totaleCommessa > 0 ? riepilogo.totaleCommessa - costoStimato : null;
          previsioneEl = { costoStimato, margineStimato, pctConsumata: Math.round(giorniPassati/durataGiorni*100) };
        }
        return (
          <div style={{background:"#fff",borderLeft:"1.5px solid #2563eb55",borderRight:"1.5px solid #2563eb55",borderBottom:"1.5px solid #2563eb55",borderTop:"4px solid #2563eb",borderRadius:12,padding:16,marginBottom:16,boxShadow:"0 4px 14px rgba(17,24,39,.07)"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#2563eb",marginBottom:12}}>📊 Preventivo vs Consuntivo</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {pctBudget !== null && (
                <div style={{flex:1,minWidth:180,background:"#f9fafb",borderRadius:8,padding:"10px 14px"}}>
                  <div style={{fontSize:11,color:"#6b7280",marginBottom:4}}>Budget previsto: <strong>€ {formatValutaItaliana(riepilogo.budgetPrevisto)}</strong></div>
                  <div style={{fontSize:11,color:"#6b7280",marginBottom:8}}>Costi attuali: <strong style={{color:pctBudget>100?"#dc2626":"#059669"}}>€ {formatValutaItaliana(riepilogo.totaleCosti)}</strong></div>
                  <div style={{background:"#e5e7eb",borderRadius:4,height:10,overflow:"hidden",marginBottom:4}}>
                    <div style={{background:pctBudget>90?"#dc2626":pctBudget>70?"#f59e0b":"#059669",height:"100%",width:`${Math.min(pctBudget,100)}%`,borderRadius:4,transition:"width 0.5s"}}/>
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:pctBudget>100?"#dc2626":pctBudget>90?"#f59e0b":"#059669"}}>
                    {pctBudget}% del budget consumato {pctBudget>100?"⚠️ SFORATO":""}
                  </div>
                </div>
              )}
              {previsioneEl && (
                <div style={{flex:1,minWidth:180,background:"#f0f9ff",borderRadius:8,padding:"10px 14px",border:"1px solid #bae6fd"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#0369a1",marginBottom:6}}>🔮 Previsione a fine commessa</div>
                  <div style={{fontSize:11,color:"#374151",marginBottom:4}}>Costo stimato totale: <strong>€ {formatValutaItaliana(Math.round(previsioneEl.costoStimato))}</strong></div>
                  {previsioneEl.margineStimato !== null && (
                    <div style={{fontSize:11,color:"#374151",marginBottom:4}}>Margine stimato: <strong style={{color:previsioneEl.margineStimato>=0?"#059669":"#dc2626"}}>€ {formatValutaItaliana(Math.round(previsioneEl.margineStimato))}</strong></div>
                  )}
                  <div style={{fontSize:11,color:"#6b7280"}}>Avanzamento temporale: {previsioneEl.pctConsumata}%</div>
                  <div style={{fontSize:10,color:"#9ca3af",marginTop:4,fontStyle:"italic"}}>Basata sul ritmo di spesa attuale</div>
                </div>
              )}
              {!pctBudget && !previsioneEl && (
                <div style={{fontSize:12,color:"#9ca3af",fontStyle:"italic"}}>
                  ℹ️ Inserisci budget previsto e date commessa per vedere il confronto e la previsione.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── LE MIE ORE: CONTABILITÀ A PARTE (W412) — già comprese nel totale Manodopera ── */}
      {diario.leggibile && (
        <ContabilitaLeMieOre blocchi={diario.blocchi} dipendenti={dipendenti} uidContati={uidDiario} numIt={numIt}
          codiceCommessa={projectData?.codiceCommessa||""} nomeCommessa={projectData?.name||""}
          onNavigate={onNavigate} puoAprireDipendenti={isOrgAdmin || isPlatformOwner || canSee("dipendenti")}/>
      )}

      {/* ── POSSIBILI DOPPIONI NEI MATERIALI (W408) ── */}
      <RiquadroDoppioniCommessa orgId={orgId} projectId={projectId} esclusi={budget?.doppioniEsclusi||[]}
        aggiorna={`${movimenti.length}-${costiMateriali.length}`} puoSegnare={canEdit}
        onEscludi={chiave => budget?.id
          ? updateDoc(doc(db,"budgetCommessa",budget.id), { doppioniEsclusi: arrayUnion(chiave) })
          : setDoc(doc(db,"budgetCommessa","budget_"+projectId), { projectId, organizationId: orgId, doppioniEsclusi: arrayUnion(chiave), createdAt: serverTimestamp() }, { merge: true })}/>

      {/* ── SEZIONI CATEGORIE ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
      {tutteLeCategorie.map(cat => (
        <React.Fragment key={cat.id}>
          <SezioneCategoria categoria={cat}
            costiOda={cat.id==="materiali"?costiMateriali:[]}
            rientriNonApplicati={cat.id==="materiali"?rientriNonApplicati:[]}
            movimenti={movimenti.filter(m=>m.categoriaId===cat.id)}
            projectId={projectId} clienteId={clienteId} orgId={orgId}
            currentUser={currentUser} canEdit={canEdit}
            dipendenti={dipendenti}
            orePerDipendente={cat.id==="personale"?orePerDipendente:[]}
            orgSettings={orgSettings}
            noleggiMezziOpera={cat.id==="noleggi_mezzi_opera"?noleggiMezziOpera:[]}
            canChiudereNoleggio={canChiudereNoleggio}
            onApriChiusuraNoleggio={(n)=>{setChiudiNoleggioTarget(n);setDataChiusuraNoleggio(toLocalDateStr(new Date()));}}/>
          {cat.id==="personale" && (
            <SubappaltatoriSection
              movimenti={movimenti.filter(m=>m.categoriaId==="subappaltatore")}
              dipendenti={dipendenti.filter(d=>d.tipo==="subappaltatore" && d.attivo!==false)}
              projectId={projectId} clienteId={clienteId} orgId={orgId}
              currentUser={currentUser} canEdit={canEdit}/>
          )}
        </React.Fragment>
      ))}
      </div>

      {/* ──TI ── */}

      {/* ── AGGIUNGI CATEGORIA CUSTOM ── */}
      {canEdit && (
        <div style={{marginTop:8}}>
          {!showAddCategoria ? (
            <button onClick={()=>setShowAddCategoria(true)}
              style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"1px dashed #d1d5db",borderRadius:8,padding:"10px 16px",cursor:"pointer",color:"#7c3aed",fontSize:13,fontWeight:600,fontFamily:"'Inter',sans-serif",width:"100%",justifyContent:"center"}}>
              <Plus size={14}/> Aggiungi categoria personalizzata
            </button>
          ) : (
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Nuova categoria</div>
              <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Icona</label>
                  <select className="input" style={{width:80}} value={nuovaCategoria.icon} onChange={e=>setNuovaCategoria(c=>({...c,icon:e.target.value}))}>
                    {ICONE_DISPONIBILI.map(i=><option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div style={{flex:1}}>
                  <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Nome categoria</label>
                  <input className="input" value={nuovaCategoria.label} onChange={e=>setNuovaCategoria(c=>({...c,label:e.target.value}))}
                    placeholder="Es. Licenze software, Consulenze, Assicurazioni..."/>
                </div>
                <button onClick={aggiungiCategoria} className="btn btn-primary">Aggiungi</button>
                <button onClick={()=>setShowAddCategoria(false)} className="btn btn-secondary">Annulla</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal chiusura noleggio — data fine effettiva libera, anche pregressa in caso di dimenticanza */}
      {chiudiNoleggioTarget && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setChiudiNoleggioTarget(null)}>
          <div className="modal" style={{maxWidth:400}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <h3 style={{fontSize:15,fontWeight:800,color:"#c2410c",margin:0}}>Chiudi noleggio</h3>
              <button className="btn-icon" onClick={()=>setChiudiNoleggioTarget(null)}><X size={15}/></button>
            </div>
            <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>
              {TIPI_MEZZO_OPERA[chiudiNoleggioTarget.tipo]?.label||chiudiNoleggioTarget.tipo} · {chiudiNoleggioTarget.fornitore}. Inserisci la data reale di fine noleggio — può essere una data passata se ci si accorge in ritardo. Il costo si bloccherà su questa data.
            </p>
            <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Data fine effettiva *</label>
            <input type="date" value={dataChiusuraNoleggio} max={toLocalDateStr(new Date())} onChange={e=>setDataChiusuraNoleggio(e.target.value)}
              style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,marginBottom:16,boxSizing:"border-box"}}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn btn-secondary" onClick={()=>setChiudiNoleggioTarget(null)}>Annulla</button>
              <button onClick={confermaChiusuraNoleggio} disabled={savingChiusuraNoleggio}
                style={{padding:"8px 20px",border:"none",borderRadius:8,background:"#c2410c",color:"#fff",fontSize:13,fontWeight:700,cursor:savingChiusuraNoleggio?"not-allowed":"pointer"}}>
                {savingChiusuraNoleggio?"...":"Conferma chiusura"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
